import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  X,
  Check,
  Plus,
  Trash2,
  Droplet,
  RotateCcw,
  AlertCircle,
  History,
  Info,
  Wrench,
  Battery,
  Edit,
  ChevronDown,
  ChevronUp,
  KeyRound,
  ShieldCheck,
  Printer,
  Download,
  Navigation,
  List,
  MapPin,
  Save,
  StickyNote
} from 'lucide-react';
import { serviceCallsAPI, serviceCallVisitsAPI, clientsAPI, serviceCallTypesAPI, cspireAPI, clientFlagsAPI, franklinAPI } from '@shared/apiClient';
import { useAuth } from '@shared/authContext';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate, formatDateTime, getStatusColor, getStatusText, getResolutionDisplay, formatCurrency, getClientDisplayName, formatCoolingType } from '@shared/utils';
import { generateServiceCallPDF, printServiceCallPDF } from '../utils/pdfGenerator';
import toast from 'react-hot-toast';
import InventorySearch from '../components/InventorySearch';
import ClientDetailsModal from '../components/ClientDetailsModal';
import SiteDetailsModal from '../components/SiteDetailsModal';
import { getDirectionsUrl } from '../components/DetailSection';
import AssignmentsMap from '../components/AssignmentsMap';

const ClientFlagIcons = ({ flags }) => {
  if (!flags || flags.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {flags.map((flag) => (
        <span
          key={flag.id}
          className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs font-semibold shadow-sm ${
            flag.isActive
              ? 'border-yellow-400 bg-yellow-100 text-yellow-900'
              : 'border-gray-300 bg-gray-100 text-gray-600'
          }`}
        >
          <FlagIcon iconName={flag.icon} className="h-4 w-4" title={flag.name} />
          <span className="uppercase tracking-wide">{flag.name}</span>
        </span>
      ))}
    </div>
  );
};

const formatCspireCoords = (site) => {
  if (!site) return null;
  const { latitude, longitude } = site;
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) return null;
  const lat = typeof latitude === 'number' ? latitude.toFixed(6) : latitude;
  const lon = typeof longitude === 'number' ? longitude.toFixed(6) : longitude;
  if (lat === '' || lon === '') return null;
  return `${lat}, ${lon}`;
};

const getCspireMapUrl = (site) => {
  if (!site) return null;
  const { latitude, longitude } = site;
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) return null;
  if (latitude === '' || longitude === '') return null;
  return `https://maps.google.com/?q=${latitude},${longitude}`;
};

const ServiceCallModal = ({ serviceCall, isOpen, onClose, isRepairCall = false, onServiceCallRefresh }) => {
  const [activeTab, setActiveTab] = useState('service'); // 'service' | 'client' | 'techNotes'
  const [techNotes, setTechNotes] = useState('');
  const [initialTechNotes, setInitialTechNotes] = useState('');
  const [isSavingTechNotes, setIsSavingTechNotes] = useState(false);
  const [formData, setFormData] = useState({
    workPerformed: '',
    partsUsed: [],
    generatorModel: '',
    generatorSerialNumber: '',
    generatorInfoUnreadable: false,
    returnReason: '',
    typeId: '',
    laborHours: '',
    generatorHours: '',
    mileage: '',
    fuelLevel: '',
    cspireScheduleId: '',
    airFilter: '',
    fuelFilter1: '',
    fuelFilter2: '',
    oilFilter1: '',
    oilFilter2: ''
  });
  const [clientFormData, setClientFormData] = useState({
    generatorType: '',
    generatorModel: '',
    generatorSerialNumber: '',
    generatorSize: '',
    generatorCoolingType: '',
    generatorExerciseTime: '',
    airFilter: '',
    fuelFilter1: '',
    fuelFilter2: '',
    oilFilter1: '',
    oilFilter2: '',
    fuelAdditive: '',
    fuelType: '',
    oilType: '',
    oilAmount: '',
    batterySize: '',
    batteryDateCode: '',
    batteryDateChanged: '',
    gateCode: false,
    gateCodeValue: ''
  });
  const [initialClientData, setInitialClientData] = useState(null);
  const [flagIds, setFlagIds] = useState([]);
  const [initialFlagIds, setInitialFlagIds] = useState([]);
  const [newPart, setNewPart] = useState({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [useCustomPart, setUseCustomPart] = useState(false);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [actionType, setActionType] = useState(null); // 'complete', 'return', or 'visit'
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [isSavingSiteInfo, setIsSavingSiteInfo] = useState(false);
  const [errors, setErrors] = useState({});
  const [cspireCustomBilling, setCspireCustomBilling] = useState(false);
  const [customLineItems, setCustomLineItems] = useState([]);
  const [newLineItem, setNewLineItem] = useState({ description: '', quantity: '1' });
  const queryClient = useQueryClient();

  // Fetch service call types
  const { data: typesData } = useQuery(
    'serviceCallTypes',
    () => serviceCallTypesAPI.getServiceCallTypes({ includeInactive: false }),
    { enabled: isOpen }
  );

  const serviceCallTypes = typesData?.data?.types || [];

  const { data: cspireSchedulesData } = useQuery(
    ['cspirePriceSchedules', serviceCall?.id],
    () => cspireAPI.getPriceSchedules({ includeInactive: false }),
    { enabled: isOpen && Boolean(serviceCall?.isCspire) }
  );

  const cspireSchedules = cspireSchedulesData?.data?.schedules || [];

  const { data: flagsData } = useQuery(
    'clientFlags',
    () => clientFlagsAPI.getClientFlags(),
    { enabled: isOpen }
  );

  useEffect(() => {
    if (serviceCall && serviceCallTypes.length > 0) {
      const client = serviceCall.client;
      const isOilChangeMonth = client?.isOilChangeMonth || false;
      const latestVisit = serviceCall.visits?.length
        ? serviceCall.visits[serviceCall.visits.length - 1]
        : null;
      const normalizePartsUsed = (parts) => {
        if (!parts) return [];
        if (Array.isArray(parts)) return parts;
        if (typeof parts === 'string') {
          try {
            const parsed = JSON.parse(parts);
            return Array.isArray(parsed) ? parsed : [];
          } catch (error) {
            return [];
          }
        }
        return [];
      };
      
      // Determine default type based on call type
      let defaultTypeId = '';
      if (isRepairCall) {
        // Default to "repair" for repair calls
        const repairType = serviceCallTypes.find(t => t.name.toLowerCase() === 'repair');
        defaultTypeId = repairType?.id || '';
      } else if (isOilChangeMonth) {
        // Default to "service" for oil change months
        const serviceType = serviceCallTypes.find(t => t.name.toLowerCase() === 'service');
        defaultTypeId = serviceType?.id || '';
      } else {
        // Default to "inspection" for non-oil change months
        const inspectionType = serviceCallTypes.find(t => t.name.toLowerCase() === 'inspection');
        defaultTypeId = inspectionType?.id || '';
      }

      // Check if there's saved draft data in localStorage
      const draftKey = `serviceCallDraft_${serviceCall.id}`;
      const savedDraft = localStorage.getItem(draftKey);
      const baseWorkPerformed = serviceCall.needsTechInfo
        ? (latestVisit?.workPerformed || serviceCall.workPerformed || '')
        : (serviceCall.workPerformed || '');
      const basePartsUsed = serviceCall.needsTechInfo
        ? (normalizePartsUsed(latestVisit?.partsUsed) || normalizePartsUsed(serviceCall.partsUsed))
        : normalizePartsUsed(serviceCall.partsUsed);
      const baseLaborHours = serviceCall.needsTechInfo
        ? (latestVisit?.laborHours ?? serviceCall.laborHours ?? '')
        : (serviceCall.laborHours || '');
      const baseGeneratorHours = serviceCall.needsTechInfo
        ? (latestVisit?.generatorHours ?? serviceCall.generatorHours ?? '')
        : (serviceCall.generatorHours || '');
      const baseMileage = serviceCall.needsTechInfo
        ? (latestVisit?.mileage ?? serviceCall.mileage ?? '')
        : (serviceCall.mileage || '');
      const baseFuelLevel = latestVisit?.fuelLevel || '';
      const baseTypeId = serviceCall.needsTechInfo
        ? (latestVisit?.typeId || serviceCall.typeId || defaultTypeId)
        : (serviceCall.typeId || defaultTypeId);

      const franklinSite = serviceCall.franklinSite;
      const cspireSite = serviceCall.isCspire ? serviceCall.cspireWorkOrder?.site : null;
      const defaultGeneratorModel = serviceCall.isFranklin && franklinSite
        ? (franklinSite.generatorModel || '')
        : serviceCall.isCspire && cspireSite
        ? (cspireSite.generatorModel || '')
        : (client?.generatorModel || '');
      const defaultGeneratorSerial = serviceCall.isFranklin && franklinSite
        ? (franklinSite.generatorSerialNumber || '')
        : serviceCall.isCspire && cspireSite
        ? (cspireSite.generatorSerialNumber || '')
        : (client?.generatorSerialNumber || '');
      const defaultCspireFilters = {
        airFilter: cspireSite?.airFilter || '',
        fuelFilter1: cspireSite?.fuelFilter1 || '',
        fuelFilter2: cspireSite?.fuelFilter2 || '',
        oilFilter1: cspireSite?.oilFilter1 || '',
        oilFilter2: cspireSite?.oilFilter2 || ''
      };
      
      if (serviceCall.needsTechInfo) {
        localStorage.removeItem(draftKey);
        setFormData({
          workPerformed: baseWorkPerformed,
          partsUsed: basePartsUsed,
          generatorModel: defaultGeneratorModel,
          generatorSerialNumber: defaultGeneratorSerial,
          generatorInfoUnreadable: false,
          returnReason: serviceCall.internalNotes || '',
          typeId: baseTypeId,
          laborHours: baseLaborHours,
          generatorHours: baseGeneratorHours,
          mileage: baseMileage,
          fuelLevel: baseFuelLevel,
          cspireScheduleId: serviceCall.cspireWorkOrder?.scheduleId || '',
          ...defaultCspireFilters
        });
      } else if (savedDraft) {
        try {
          const parsedDraft = JSON.parse(savedDraft);
          // Don't let a stale draft blank out generator info that is already on file
          // for the client / Franklin site / CSpire site.
          if (!parsedDraft.generatorModel) parsedDraft.generatorModel = defaultGeneratorModel;
          if (!parsedDraft.generatorSerialNumber) parsedDraft.generatorSerialNumber = defaultGeneratorSerial;
          setFormData({
            airFilter: '',
            fuelFilter1: '',
            fuelFilter2: '',
            oilFilter1: '',
            oilFilter2: '',
            fuelLevel: '',
            ...defaultCspireFilters,
            ...parsedDraft
          });
          toast.success('Draft restored!', { duration: 2000 });
        } catch (error) {
          console.error('Error parsing saved draft:', error);
          // Fall back to default data
          setFormData({
            workPerformed: baseWorkPerformed,
            partsUsed: basePartsUsed,
            generatorModel: defaultGeneratorModel,
            generatorSerialNumber: defaultGeneratorSerial,
            generatorInfoUnreadable: false,
            returnReason: serviceCall.internalNotes || '',
            typeId: baseTypeId,
            laborHours: baseLaborHours,
            generatorHours: baseGeneratorHours,
            mileage: baseMileage,
            fuelLevel: baseFuelLevel,
            cspireScheduleId: serviceCall.cspireWorkOrder?.scheduleId || '',
            ...defaultCspireFilters
          });
        }
      } else {
        setFormData({
          workPerformed: baseWorkPerformed,
          partsUsed: basePartsUsed,
          generatorModel: defaultGeneratorModel,
          generatorSerialNumber: defaultGeneratorSerial,
          generatorInfoUnreadable: false,
          returnReason: serviceCall.internalNotes || '',
          typeId: baseTypeId,
          laborHours: baseLaborHours,
          generatorHours: baseGeneratorHours,
          mileage: baseMileage,
          fuelLevel: baseFuelLevel,
          cspireScheduleId: serviceCall.cspireWorkOrder?.scheduleId || '',
          ...defaultCspireFilters
        });
      }
      
      // Initialize client/site data for the edit tab
      if (serviceCall.isCspire && cspireSite) {
        const siteData = {
          generatorType: cspireSite.generatorType || '',
          generatorModel: cspireSite.generatorModel || '',
          generatorSerialNumber: cspireSite.generatorSerialNumber || '',
          generatorSize: cspireSite.generatorSize || '',
          generatorCoolingType: cspireSite.generatorCoolingType || '',
          generatorExerciseTime: cspireSite.generatorExerciseTime || '',
          airFilter: cspireSite.airFilter || '',
          fuelFilter1: cspireSite.fuelFilter1 || '',
          fuelFilter2: cspireSite.fuelFilter2 || '',
          oilFilter1: cspireSite.oilFilter1 || '',
          oilFilter2: cspireSite.oilFilter2 || '',
          fuelAdditive: cspireSite.fuelAdditive || '',
          fuelType: cspireSite.fuelType || '',
          oilType: cspireSite.oilType || '',
          oilAmount: cspireSite.oilAmount || '',
          batterySize: cspireSite.batterySize || '',
          batteryDateCode: cspireSite.batteryDateCode || '',
          batteryDateChanged: cspireSite.batteryDateChanged ? new Date(cspireSite.batteryDateChanged).toISOString().split('T')[0] : '',
          gateCode: false,
          gateCodeValue: ''
        };
        setClientFormData(siteData);
        setInitialClientData(siteData);
      } else if (client) {
        const clientData = {
          generatorType: client.generatorType || '',
          generatorModel: client.generatorModel || '',
          generatorSerialNumber: client.generatorSerialNumber || '',
          generatorSize: client.generatorSize || '',
          generatorCoolingType: client.generatorCoolingType || '',
          generatorExerciseTime: client.generatorExerciseTime || '',
          airFilter: client.airFilter || '',
          fuelFilter1: client.fuelFilter1 || '',
          fuelFilter2: client.fuelFilter2 || '',
          oilFilter1: client.oilFilter1 || '',
          oilFilter2: client.oilFilter2 || '',
          fuelAdditive: client.fuelAdditive || '',
          fuelType: client.fuelType || '',
          oilType: client.oilType || '',
          oilAmount: client.oilAmount || '',
          batterySize: client.batterySize || '',
          batteryDateCode: client.batteryDateCode || '',
          batteryDateChanged: client.batteryDateChanged ? new Date(client.batteryDateChanged).toISOString().split('T')[0] : '',
          gateCode: client.gateCode || false,
          gateCodeValue: client.gateCodeValue || ''
        };
        setClientFormData(clientData);
        setInitialClientData(clientData);

        const currentFlagIds = client.flags?.map(f => f.id) || [];
        setFlagIds(currentFlagIds);
        setInitialFlagIds(currentFlagIds);
      }

      const seededTechNotes = serviceCall.isFranklin
        ? (franklinSite?.notes || '')
        : serviceCall.isCspire
          ? (cspireSite?.notes || '')
          : (client?.notes || '');
      setTechNotes(seededTechNotes);
      setInitialTechNotes(seededTechNotes);

      setActionType(null);
      setErrors({});
    }
  }, [serviceCall, serviceCallTypes, isRepairCall]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
    if (serviceCall && isOpen && formData.workPerformed !== undefined) {
      const draftKey = `serviceCallDraft_${serviceCall.id}`;
      localStorage.setItem(draftKey, JSON.stringify(formData));
    }
  }, [formData, serviceCall, isOpen]);

  const updateServiceCallMutation = useMutation(
    (data) => serviceCallsAPI.updateServiceCall(serviceCall.id, data),
    {
      onSuccess: (response, variables) => {
        const isCompleted = variables.status === 'completed';
        // Check if it's a visit by checking the type name in the response or variables
        const visitType = serviceCallTypes.find(t => t.name.toLowerCase() === 'visit');
        const isVisit = visitType && (variables.typeId === visitType.id || response?.data?.serviceCall?.type?.name?.toLowerCase() === 'visit');
        let message = '';
        if (isVisit) {
          message = 'Service call marked as visit successfully!';
        } else if (isCompleted) {
          message = 'Service call completed successfully!';
        } else {
          message = 'Service call marked as return';
        }
        
        // Clear the saved draft from localStorage
        const draftKey = `serviceCallDraft_${serviceCall.id}`;
        localStorage.removeItem(draftKey);
        
        toast.success(message);
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('myServiceCalls');
        queryClient.invalidateQueries('attentionNeededServiceCalls');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update service call');
      }
    }
  );

  const updateClientMutation = useMutation(
    (data) => clientsAPI.updateClientGeneratorInfo(serviceCall.client.id, data),
    {
      onSuccess: () => {
        toast.success('Customer information updated successfully!');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries('allClients');
      },
      onError: (error) => {
        console.error('Failed to update customer generator info:', error);
        toast.error(error.response?.data?.message || 'Failed to update customer');
        // Don't show error toast for client update failures, just log it
      }
    }
  );

  const handleInventorySelect = (inventoryItem) => {
    setSelectedInventoryItem(inventoryItem);
    setNewPart({
      inventoryId: inventoryItem.inventoryId || '',
      itemNumber: inventoryItem.itemNumber,
      description: inventoryItem.description,
      name: inventoryItem.itemNumber,
      quantity: inventoryItem.quantity || 1,
      cost: inventoryItem.price || 0
    });
    setUseCustomPart(false); // Switch back to inventory mode when item is selected
  };

  const handleAddPart = () => {
    if (useCustomPart) {
      // Custom part mode - require name, quantity, and cost
      if (newPart.name && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            itemNumber: newPart.name, // Use name as itemNumber for custom parts
            description: newPart.description || newPart.name,
            name: newPart.name,
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
        setSearchResetKey(k => k + 1);
      } else {
        toast.error('Please enter part name, quantity, and cost');
      }
    } else {
      // Inventory mode - require selected item and quantity
      if (newPart.itemNumber && newPart.quantity && newPart.cost !== undefined) {
        setFormData({
          ...formData,
          partsUsed: [...formData.partsUsed, {
            ...(newPart.inventoryId ? { inventoryId: newPart.inventoryId } : {}),
            itemNumber: newPart.itemNumber,
            description: newPart.description || newPart.name,
            name: newPart.itemNumber, // For backward compatibility
            quantity: parseFloat(newPart.quantity),
            cost: parseFloat(newPart.cost)
          }]
        });
        setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
        setSelectedInventoryItem(null);
        setSearchResetKey(k => k + 1);
      } else {
        toast.error('Please select a part and enter quantity');
      }
    }
  };

  const handleToggleMode = () => {
    setUseCustomPart(!useCustomPart);
    setSelectedInventoryItem(null);
    setNewPart({ inventoryId: '', itemNumber: '', description: '', name: '', quantity: '', cost: '' });
  };

  const handleRemovePart = (index) => {
    setFormData({
      ...formData,
      partsUsed: formData.partsUsed.filter((_, i) => i !== index)
    });
  };

  const handleClearDraft = () => {
    if (window.confirm('Are you sure you want to clear the saved draft and start fresh?')) {
      const draftKey = `serviceCallDraft_${serviceCall.id}`;
      localStorage.removeItem(draftKey);
      
      // Reset form to initial defaults
      const client = serviceCall.client;
      const isOilChangeMonth = client?.isOilChangeMonth || false;
      
      let defaultTypeId = '';
      if (isRepairCall) {
        const repairType = serviceCallTypes.find(t => t.name.toLowerCase() === 'repair');
        defaultTypeId = repairType?.id || '';
      } else if (isOilChangeMonth) {
        const serviceType = serviceCallTypes.find(t => t.name.toLowerCase() === 'service');
        defaultTypeId = serviceType?.id || '';
      } else {
        const inspectionType = serviceCallTypes.find(t => t.name.toLowerCase() === 'inspection');
        defaultTypeId = inspectionType?.id || '';
      }

      const fs = serviceCall.franklinSite;
      const cs = serviceCall.isCspire ? serviceCall.cspireWorkOrder?.site : null;
      const clearGenModel = serviceCall.isFranklin && fs
        ? (fs.generatorModel || '')
        : serviceCall.isCspire && cs
        ? (cs.generatorModel || '')
        : (client?.generatorModel || '');
      const clearGenSerial = serviceCall.isFranklin && fs
        ? (fs.generatorSerialNumber || '')
        : serviceCall.isCspire && cs
        ? (cs.generatorSerialNumber || '')
        : (client?.generatorSerialNumber || '');
      setFormData({
        workPerformed: serviceCall.workPerformed || '',
        partsUsed: serviceCall.partsUsed || [],
        generatorModel: clearGenModel,
        generatorSerialNumber: clearGenSerial,
        generatorInfoUnreadable: false,
        returnReason: serviceCall.internalNotes || '',
        typeId: serviceCall.typeId || defaultTypeId,
        laborHours: serviceCall.laborHours || '',
        generatorHours: serviceCall.generatorHours || '',
        mileage: serviceCall.mileage || '',
        fuelLevel: '',
        cspireScheduleId: serviceCall.cspireWorkOrder?.scheduleId || '',
        airFilter: cs?.airFilter || '',
        fuelFilter1: cs?.fuelFilter1 || '',
        fuelFilter2: cs?.fuelFilter2 || '',
        oilFilter1: cs?.oilFilter1 || '',
        oilFilter2: cs?.oilFilter2 || ''
      });
      setActionType(null);
      setErrors({});
      setCspireCustomBilling(false);
      setCustomLineItems([]);
      setNewLineItem({ description: '', quantity: '1' });
      toast.success('Draft cleared!');
    }
  };

  const validateForm = (action) => {
    const newErrors = {};

    if (!formData.workPerformed.trim()) {
      newErrors.workPerformed = 'Work performed is required';
    }

    if (serviceCall?.isCspire && !formData.cspireScheduleId && !cspireCustomBilling) {
      newErrors.cspireScheduleId = 'Price schedule is required (or choose custom billing)';
    }

    // For visits, only description is required (no parts, no generator info)
    if (action === 'visit') {
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Generator info validation - required unless marked as unreadable (not for visits).
    // Skip the requirement entirely if the model + serial are already on file for the
    // client / Franklin site / CSpire site, since the techs are not being asked to
    // capture new info in that case.
    const client = serviceCall?.client;
    const franklinSite = serviceCall?.franklinSite;
    const cspireSite = serviceCall?.cspireWorkOrder?.site;
    const generatorInfoOnFile =
      (client?.generatorModel && client?.generatorSerialNumber) ||
      (franklinSite?.generatorModel && franklinSite?.generatorSerialNumber) ||
      (cspireSite?.generatorModel && cspireSite?.generatorSerialNumber);
    if (
      !serviceCall?.isCspire &&
      !serviceCall?.isFranklin &&
      !formData.generatorInfoUnreadable &&
      !generatorInfoOnFile
    ) {
      if (!formData.generatorModel.trim()) {
        newErrors.generatorModel = 'Generator model is required';
      }
      if (!formData.generatorSerialNumber.trim()) {
        newErrors.generatorSerialNumber = 'Generator serial number is required';
      }
    }

    // Return reason validation if marking as return
    if (action === 'return' && !formData.returnReason.trim()) {
      newErrors.returnReason = 'Return reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClientFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    
    const updateData = {};
    
    Object.keys(clientFormData).forEach(key => {
      if (serviceCall?.isCspire && (key === 'gateCode' || key === 'gateCodeValue')) return;
      if (clientFormData[key] !== initialClientData[key]) {
        if (typeof clientFormData[key] === 'boolean') {
          updateData[key] = clientFormData[key];
        } else {
          updateData[key] = clientFormData[key]?.trim() || null;
        }
      }
    });

    if (!serviceCall?.isCspire) {
      const flagsChanged = JSON.stringify([...flagIds].sort()) !== JSON.stringify([...initialFlagIds].sort());
      if (flagsChanged) {
        updateData.flagIds = flagIds;
      }
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    try {
      if (serviceCall?.isCspire) {
        const siteId = serviceCall.cspireWorkOrder?.site?.id;
        if (!siteId) {
          toast.error('No C Spire site linked to this work order');
          return;
        }
        setIsSavingSiteInfo(true);
        await cspireAPI.updateSiteByTechnician(siteId, updateData);
        toast.success('Site information updated successfully!');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('cspireWorkOrders');
        queryClient.invalidateQueries('attentionNeededServiceCalls');
      } else {
        await updateClientMutation.mutateAsync(updateData);
      }
    } catch (error) {
      if (serviceCall?.isCspire) {
        toast.error(error.response?.data?.message || 'Failed to update site information');
      }
    } finally {
      if (serviceCall?.isCspire) setIsSavingSiteInfo(false);
    }
  };

  const handleSaveTechNotes = async (e) => {
    e?.preventDefault?.();
    const trimmed = (techNotes || '').trim();
    if (trimmed === (initialTechNotes || '').trim()) {
      toast('No changes to save', { icon: 'ℹ️' });
      return;
    }
    setIsSavingTechNotes(true);
    try {
      const payload = trimmed.length > 0 ? trimmed : null;
      if (serviceCall?.isFranklin) {
        const siteId = serviceCall.franklinSite?.id;
        if (!siteId) {
          toast.error('No Franklin site linked to this assignment');
          return;
        }
        await franklinAPI.updateSiteTechNotes(siteId, payload);
      } else if (serviceCall?.isCspire) {
        const siteId = serviceCall.cspireWorkOrder?.site?.id;
        if (!siteId) {
          toast.error('No CSpire site linked to this work order');
          return;
        }
        await cspireAPI.updateSiteTechNotes(siteId, payload);
      } else {
        const clientId = serviceCall?.client?.id;
        if (!clientId) {
          toast.error('No client linked to this assignment');
          return;
        }
        await clientsAPI.updateTechNotes(clientId, payload);
      }
      setInitialTechNotes(trimmed);
      toast.success('Tech notes saved');
      queryClient.invalidateQueries('weeklyAssignments');
      queryClient.invalidateQueries('priorityAssignments');
      queryClient.invalidateQueries('clients');
      queryClient.invalidateQueries('allClients');
      queryClient.invalidateQueries('cspireWorkOrders');
      queryClient.invalidateQueries('franklinSites');
    } catch (error) {
      console.error('Failed to save tech notes:', error);
      toast.error(error.response?.data?.message || 'Failed to save tech notes');
    } finally {
      setIsSavingTechNotes(false);
    }
  };

  const handleSubmit = async (e, action) => {
    e.preventDefault();

    let submitAction = action || actionType;
    if (!submitAction) {
      toast.error('Please select an action: Complete Service or Mark as Return');
      return;
    }

    if (serviceCall?.needsTechInfo) {
      submitAction = 'complete';
    }

    if (!validateForm(submitAction)) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    const client = serviceCall.client;

    // Update client generator info if provided and not unreadable
    if (!serviceCall?.isCspire && !serviceCall?.isFranklin && client && !formData.generatorInfoUnreadable && formData.generatorModel && formData.generatorSerialNumber) {
      // Only update if the values have changed
      if (client.generatorModel !== formData.generatorModel || 
          client.generatorSerialNumber !== formData.generatorSerialNumber) {
        try {
          await updateClientMutation.mutateAsync({
            generatorModel: formData.generatorModel.trim(),
            generatorSerialNumber: formData.generatorSerialNumber.trim()
          });
        } catch (error) {
          // Continue even if customer update fails
          console.error('Failed to update customer generator info:', error);
        }
      }
    }

    if (serviceCall?.isFranklin && serviceCall?.franklinSite?.id && !formData.generatorInfoUnreadable) {
      const fs = serviceCall.franklinSite;
      const model = formData.generatorModel?.trim() || '';
      const serial = formData.generatorSerialNumber?.trim() || '';
      if (model !== (fs.generatorModel || '') || serial !== (fs.generatorSerialNumber || '')) {
        try {
          await franklinAPI.updateSiteByTechnician(serviceCall.franklinSite.id, {
            generatorModel: model || null,
            generatorSerialNumber: serial || null
          });
          queryClient.invalidateQueries('franklinSites');
        } catch (error) {
          console.error('Failed to update Franklin site generator info:', error);
        }
      }
    }

    if (serviceCall?.isCspire && serviceCall.cspireWorkOrder?.site?.id) {
      try {
        const site = serviceCall.cspireWorkOrder.site;
        const payload = {};
        const consider = (key) => {
          const raw = formData[key];
          const t = typeof raw === 'string' ? raw.trim() : raw;
          const cur = site[key];
          const curStr = cur == null ? '' : String(cur);
          if (t !== curStr) payload[key] = t || null;
        };
        [
          'generatorModel',
          'generatorSerialNumber',
          'airFilter',
          'fuelFilter1',
          'fuelFilter2',
          'oilFilter1',
          'oilFilter2'
        ].forEach(consider);
        if (Object.keys(payload).length > 0) {
          await cspireAPI.updateSiteByTechnician(site.id, payload);
          queryClient.invalidateQueries('cspireWorkOrders');
        }
      } catch (error) {
        console.error('Failed to update C Spire site equipment:', error);
        toast.error('Could not save generator/filters to the C Spire site. Your service report may still be saved.');
      }
    }

    // Persist tech draft lines for custom C Spire billing
    if (serviceCall?.isCspire && cspireCustomBilling && customLineItems.length > 0) {
      try {
        const draftInvoiceData = {
          ...(serviceCall.invoiceData || {}),
          lineItems: customLineItems.map(item => ({
            description: item.description,
            quantity: parseFloat(item.quantity) || 1,
            unitPrice: null,
            itemNumber: item.itemNumber || ''
          }))
        };
        await serviceCallsAPI.updateServiceCall(serviceCall.id, { invoiceData: draftInvoiceData });
      } catch (error) {
        console.error('Failed to save custom billing draft:', error);
      }
    }

    // Prepare visit data
    const visitData = {
      workPerformed: formData.workPerformed.trim(),
      outcome: submitAction === 'visit' || submitAction === 'complete' ? 'completed' : 'needs_return',
      reasonForReturn: submitAction === 'return' ? formData.returnReason.trim() : null
    };

    // Always include parts on the visit so they show in history & invoices
    if (formData.partsUsed.length > 0) {
      visitData.partsUsed = formData.partsUsed;
    }

    // For visits, set type to "visit"
    if (submitAction === 'visit') {
      const visitType = serviceCallTypes.find(t => t.name.toLowerCase() === 'visit');
      if (visitType) {
        visitData.typeId = visitType.id;
      }
    } else {
      if (formData.typeId) {
        visitData.typeId = formData.typeId;
      }
      if (formData.laborHours) {
        visitData.laborHours = parseFloat(formData.laborHours);
      }
      if (formData.generatorHours) {
        visitData.generatorHours = parseFloat(formData.generatorHours);
      }
      if (formData.mileage) {
        visitData.mileage = parseFloat(formData.mileage);
      }
    }

    // Fuel level is a CSpire-only per-visit reading
    if (serviceCall?.isCspire) {
      const trimmedFuelLevel = (formData.fuelLevel || '').trim();
      visitData.fuelLevel = trimmedFuelLevel === '' ? null : trimmedFuelLevel;
    }

    const patchCspireScheduleAfterVisit = async () => {
      if (!serviceCall?.isCspire || !serviceCall?.cspireWorkOrder?.id) return;
      try {
        const rawScheduleId = formData.cspireScheduleId;
        const schedulePayload =
          cspireCustomBilling || rawScheduleId === '' || rawScheduleId === undefined
            ? { scheduleId: null }
            : { scheduleId: rawScheduleId };
        await cspireAPI.updateWorkOrderSchedule(serviceCall.cspireWorkOrder.id, schedulePayload);
      } catch (error) {
        const detail =
          error.response?.data?.message
          || (Array.isArray(error.response?.data?.errors) && error.response.data.errors.map((e) => e.msg).join('; '))
          || error.message
          || 'Failed to update CSpire schedule';
        toast.error(
          `${detail} — Your visit was saved; an admin can adjust the price schedule on the work order if needed.`,
          { duration: 8000 }
        );
      }
    };

    try {
      if (serviceCall?.needsTechInfo) {
        const latestVisit = serviceCall.visits?.length
          ? serviceCall.visits[serviceCall.visits.length - 1]
          : null;

        if (!latestVisit?.id) {
          toast.error('No existing visit found to update');
          return;
        }

        const updateVisitData = {
          ...visitData,
          outcome: 'completed'
        };

        if (!updateVisitData.reasonForReturn) {
          delete updateVisitData.reasonForReturn;
        }

        await serviceCallVisitsAPI.updateVisit(serviceCall.id, latestVisit.id, updateVisitData);

        const updateData = {
          status: 'completed',
          workPerformed: formData.workPerformed.trim(),
          needsTechInfo: false,
          techInfoUpdatedAt: new Date().toISOString()
        };

        if (formData.partsUsed.length > 0) {
          updateData.partsUsed = formData.partsUsed;
        }
        if (formData.laborHours) {
          updateData.laborHours = parseFloat(formData.laborHours);
        }
        if (formData.generatorHours) {
          updateData.generatorHours = parseFloat(formData.generatorHours);
        }
        if (formData.mileage) {
          updateData.mileage = parseFloat(formData.mileage);
        }
        if (visitData.typeId) {
          updateData.typeId = visitData.typeId;
        }

        await updateServiceCallMutation.mutateAsync(updateData);
        await patchCspireScheduleAfterVisit();
        return;
      }

      // Create the visit record first
      await serviceCallVisitsAPI.createVisit(serviceCall.id, visitData);

      // Then update the service call status and typeId
      const updateData = {
        status: submitAction === 'visit' || submitAction === 'complete' ? 'completed' : 'return'
      };

      if (submitAction === 'visit' || submitAction === 'complete') {
        updateData.completedDate = new Date().toISOString();
      }

      // Clear tech info request when resubmitting completion/visit
      if ((submitAction === 'visit' || submitAction === 'complete') && serviceCall.needsTechInfo) {
        updateData.needsTechInfo = false;
        updateData.techInfoUpdatedAt = new Date().toISOString();
      }

      // Include typeId in the service call update
      if (visitData.typeId) {
        updateData.typeId = visitData.typeId;
      }

      // Update the service call
      await updateServiceCallMutation.mutateAsync(updateData);
      await patchCspireScheduleAfterVisit();
    } catch (error) {
      console.error('Error creating visit or updating service call:', error);
      toast.error(error.response?.data?.message || 'Failed to save visit information');
    }
  };

  if (!isOpen) return null;

  const client = serviceCall?.client;
  const cspireWorkOrder = serviceCall?.cspireWorkOrder;
  const franklinSite = serviceCall?.franklinSite;
  const hasExistingGeneratorInfo =
    (client?.generatorModel && client?.generatorSerialNumber) ||
    (franklinSite?.generatorModel && franklinSite?.generatorSerialNumber) ||
    (cspireWorkOrder?.site?.generatorModel && cspireWorkOrder?.site?.generatorSerialNumber);
  const displayName = serviceCall?.isFranklin
    ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
    : serviceCall?.isCspire
    ? `CSpire - ${cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'Site'}`
    : getClientDisplayName(client);

  const buildServiceCallDraftPayload = () => ({
    workPerformed: formData.workPerformed.trim(),
    partsUsed: formData.partsUsed.length > 0 ? formData.partsUsed : null,
    laborHours: formData.laborHours === '' || formData.laborHours == null ? null : parseFloat(formData.laborHours),
    generatorHours: formData.generatorHours === '' || formData.generatorHours == null ? null : parseFloat(formData.generatorHours),
    mileage: formData.mileage === '' || formData.mileage == null ? null : parseFloat(formData.mileage),
    fuelLevel: serviceCall?.isCspire
      ? ((formData.fuelLevel || '').trim() || null)
      : null,
    typeId: formData.typeId || null
  });

  const buildMergedServiceCallForPrint = (full) => {
    const merged = { ...full };
    merged.workPerformed = formData.workPerformed ?? merged.workPerformed;
    if (formData.partsUsed?.length) merged.partsUsed = formData.partsUsed;
    if (formData.laborHours !== '' && formData.laborHours != null) {
      merged.laborHours = parseFloat(formData.laborHours);
    }
    if (formData.generatorHours !== '' && formData.generatorHours != null) {
      merged.generatorHours = parseFloat(formData.generatorHours);
    }
    if (formData.mileage !== '' && formData.mileage != null) {
      merged.mileage = parseFloat(formData.mileage);
    }
    if (formData.typeId) {
      merged.typeId = formData.typeId;
      const typeMatch = serviceCallTypes.find((t) => t.id === formData.typeId);
      if (typeMatch) merged.type = typeMatch;
    }
    if (merged.visits?.length) {
      const visits = [...merged.visits];
      const idx = visits.length - 1;
      const last = visits[idx];
      const typeMatch = formData.typeId ? serviceCallTypes.find((t) => t.id === formData.typeId) : null;
      visits[idx] = {
        ...last,
        workPerformed: formData.workPerformed || last.workPerformed,
        partsUsed: formData.partsUsed?.length ? formData.partsUsed : last.partsUsed,
        laborHours:
          formData.laborHours !== '' && formData.laborHours != null
            ? parseFloat(formData.laborHours)
            : last.laborHours,
        generatorHours:
          formData.generatorHours !== '' && formData.generatorHours != null
            ? parseFloat(formData.generatorHours)
            : last.generatorHours,
        mileage:
          formData.mileage !== '' && formData.mileage != null ? parseFloat(formData.mileage) : last.mileage,
        fuelLevel: merged.isCspire
          ? ((formData.fuelLevel || '').trim() || last.fuelLevel || null)
          : last.fuelLevel,
        typeId: formData.typeId || last.typeId,
        type: typeMatch || last.type
      };
      merged.visits = visits;
    }
    if (merged.isFranklin && merged.franklinSite) {
      merged.franklinSite = {
        ...merged.franklinSite,
        generatorModel: formData.generatorModel?.trim() || merged.franklinSite.generatorModel,
        generatorSerialNumber: formData.generatorSerialNumber?.trim() || merged.franklinSite.generatorSerialNumber
      };
    }
    if (merged.isCspire && merged.cspireWorkOrder?.site) {
      const s = merged.cspireWorkOrder.site;
      merged.cspireWorkOrder = {
        ...merged.cspireWorkOrder,
        site: {
          ...s,
          generatorModel: formData.generatorModel?.trim() || s.generatorModel,
          generatorSerialNumber: formData.generatorSerialNumber?.trim() || s.generatorSerialNumber,
          airFilter: formData.airFilter?.trim() || s.airFilter,
          fuelFilter1: formData.fuelFilter1?.trim() || s.fuelFilter1,
          fuelFilter2: formData.fuelFilter2?.trim() || s.fuelFilter2,
          oilFilter1: formData.oilFilter1?.trim() || s.oilFilter1,
          oilFilter2: formData.oilFilter2?.trim() || s.oilFilter2
        }
      };
    }
    return merged;
  };

  const handleSaveProgress = async () => {
    if (!serviceCall?.id) return;
    setIsSavingProgress(true);
    try {
      if (serviceCall.isFranklin && serviceCall.franklinSite?.id && !formData.generatorInfoUnreadable) {
        const fs = serviceCall.franklinSite;
        const model = formData.generatorModel?.trim() || '';
        const serial = formData.generatorSerialNumber?.trim() || '';
        if (model !== (fs.generatorModel || '') || serial !== (fs.generatorSerialNumber || '')) {
          await franklinAPI.updateSiteByTechnician(serviceCall.franklinSite.id, {
            generatorModel: model || null,
            generatorSerialNumber: serial || null
          });
          queryClient.invalidateQueries('franklinSites');
        }
      }

      if (serviceCall.isCspire && serviceCall.cspireWorkOrder?.site?.id) {
        const site = serviceCall.cspireWorkOrder.site;
        const payload = {};
        const consider = (key) => {
          const raw = formData[key];
          const t = typeof raw === 'string' ? raw.trim() : raw;
          const cur = site[key];
          const curStr = cur == null ? '' : String(cur);
          if (t !== curStr) payload[key] = t || null;
        };
        [
          'generatorModel',
          'generatorSerialNumber',
          'airFilter',
          'fuelFilter1',
          'fuelFilter2',
          'oilFilter1',
          'oilFilter2'
        ].forEach(consider);
        if (Object.keys(payload).length > 0) {
          await cspireAPI.updateSiteByTechnician(site.id, payload);
          queryClient.invalidateQueries('cspireWorkOrders');
        }
      }

      if (serviceCall.isCspire && serviceCall.cspireWorkOrder?.id) {
        const rawScheduleId = formData.cspireScheduleId;
        const schedulePayload =
          cspireCustomBilling || rawScheduleId === '' || rawScheduleId === undefined
            ? { scheduleId: null }
            : { scheduleId: rawScheduleId };
        await cspireAPI.updateWorkOrderSchedule(serviceCall.cspireWorkOrder.id, schedulePayload);
      }

      if (serviceCall.isCspire && cspireCustomBilling && customLineItems.length > 0) {
        const draftInvoiceData = {
          ...(serviceCall.invoiceData || {}),
          lineItems: customLineItems.map((item) => ({
            description: item.description,
            quantity: parseFloat(item.quantity) || 1,
            unitPrice: null,
            itemNumber: item.itemNumber || ''
          }))
        };
        await serviceCallsAPI.updateServiceCall(serviceCall.id, { invoiceData: draftInvoiceData });
      }

      const draft = buildServiceCallDraftPayload();

      if (serviceCall.needsTechInfo) {
        const latestVisit = serviceCall.visits?.length
          ? serviceCall.visits[serviceCall.visits.length - 1]
          : null;
        if (!latestVisit?.id) {
          toast.error('No visit found to update');
          return;
        }
        await serviceCallVisitsAPI.updateVisit(serviceCall.id, latestVisit.id, {
          workPerformed: draft.workPerformed,
          partsUsed: draft.partsUsed,
          laborHours: draft.laborHours,
          generatorHours: draft.generatorHours,
          mileage: draft.mileage,
          typeId: draft.typeId
        });
        await serviceCallsAPI.updateServiceCall(serviceCall.id, draft);
      } else {
        await serviceCallsAPI.updateServiceCall(serviceCall.id, draft);
      }

      toast.success('Progress saved');
      queryClient.invalidateQueries('weeklyAssignments');
      queryClient.invalidateQueries('priorityAssignments');
      queryClient.invalidateQueries('myServiceCalls');
      queryClient.invalidateQueries('attentionNeededServiceCalls');

      const refreshed = await serviceCallsAPI.getServiceCall(serviceCall.id);
      onServiceCallRefresh?.(refreshed.data.serviceCall);
    } catch (error) {
      console.error('Save progress error:', error);
      toast.error(error.response?.data?.message || 'Failed to save progress');
    } finally {
      setIsSavingProgress(false);
    }
  };

  const handleDashboardPrint = async () => {
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      const fullServiceCall = response.data.serviceCall;
      await printServiceCallPDF(buildMergedServiceCallForPrint(fullServiceCall));
    } catch (error) {
      toast.error('Failed to generate print preview');
    }
  };

  const handleDashboardDownloadPDF = async () => {
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      const fullServiceCall = response.data.serviceCall;
      await generateServiceCallPDF(buildMergedServiceCallForPrint(fullServiceCall));
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="fixed inset-0 flex h-full w-full flex-col bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {activeTab === 'service'
              ? (serviceCall?.needsTechInfo ? 'Record Service Call Again' : 'Record Service Call')
              : activeTab === 'techNotes'
                ? 'Tech Notes'
                : serviceCall?.isCspire ? 'Edit Site Information' : 'Edit Customer Information'}
          </h3>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleDashboardPrint}
              className="icon-btn"
              title="Print"
            >
              <Printer className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleDashboardDownloadPDF}
              className="icon-btn"
              title="Download PDF"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="icon-btn"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {activeTab === 'service' && (
            <p className="mt-2 text-xs text-slate-500 max-w-xl">
              Save progress stores parts, notes, and hours on the server before you complete the service. Print and download use your current form entries together with saved data.
            </p>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex -mb-px gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('service')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'service'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-1" />
              Record Service
            </button>
            {!serviceCall?.isFranklin && (
              <button
                type="button"
                onClick={() => setActiveTab('client')}
                className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                  activeTab === 'client'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Wrench className="inline h-4 w-4 mr-1" />
                {serviceCall?.isCspire ? 'Edit Site' : 'Edit Customer'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setActiveTab('techNotes')}
              className={`py-3 px-5 text-base font-semibold border-b-2 transition-colors ${
                activeTab === 'techNotes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <StickyNote className="inline h-4 w-4 mr-1" />
              Tech Notes
            </button>
          </nav>
        </div>

        <div className="flex-1 min-h-0">
          {activeTab === 'service' ? (
            <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
              <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
                {serviceCall?.needsTechInfo && serviceCall.techInfoRequestReason && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-yellow-900">Attention Needed</p>
                    <p className="text-sm text-yellow-800 whitespace-pre-wrap mt-1">
                      {serviceCall.techInfoRequestReason}
                    </p>
                    {serviceCall.techInfoRequestedAt && (
                      <p className="text-xs text-yellow-700 mt-2">
                        Requested: {formatDateTime(serviceCall.techInfoRequestedAt)}
                      </p>
                    )}
                  </div>
                )}
                {/* Client Info (Read-only) */}
                <div className="bg-gray-50 p-4 rounded-xl">
            <p className="text-sm font-medium text-gray-700">
              <span className="font-semibold">Customer:</span> {displayName}
            </p>
            {serviceCall?.isFranklin && franklinSite ? (
              <div className="text-sm text-gray-600 mt-1 space-y-1">
                <p><span className="font-semibold">Site ID:</span> {franklinSite.siteId}</p>
                <p><span className="font-semibold">Company:</span> {franklinSite.company}</p>
                {franklinSite.address && (
                  <p><span className="font-semibold">Address:</span> {franklinSite.address}, {franklinSite.city}, {franklinSite.state} {franklinSite.zipCode}</p>
                )}
                {franklinSite.backupPower && (
                  <p><span className="font-semibold">Backup Power:</span> {franklinSite.backupPower}</p>
                )}
                {franklinSite.tankSize && (
                  <p><span className="font-semibold">Tank Size:</span> {franklinSite.tankSize}</p>
                )}
                {franklinSite.fuelType && (
                  <p><span className="font-semibold">Fuel Type:</span> {franklinSite.fuelType}</p>
                )}
                {franklinSite.lastServiceDate && (
                  <p><span className="font-semibold">Last Service:</span> {formatDate(franklinSite.lastServiceDate)}</p>
                )}
                {(franklinSite.generatorType || franklinSite.generatorModel || franklinSite.generatorSerialNumber) && (
                  <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md p-2 space-y-1">
                    <p className="font-semibold text-emerald-900 text-xs uppercase tracking-wide">Equipment</p>
                    {franklinSite.generatorType && <p><span className="font-semibold">Type:</span> {franklinSite.generatorType}</p>}
                    {franklinSite.generatorModel && <p><span className="font-semibold">Model:</span> {franklinSite.generatorModel}</p>}
                    {franklinSite.generatorSerialNumber && <p><span className="font-semibold">Serial:</span> {franklinSite.generatorSerialNumber}</p>}
                    {franklinSite.generatorSize && <p><span className="font-semibold">Size:</span> {franklinSite.generatorSize}</p>}
                    {franklinSite.generatorCoolingType && <p><span className="font-semibold">Cooling:</span> {formatCoolingType(franklinSite.generatorCoolingType)}</p>}
                    {franklinSite.generatorExerciseTime && <p><span className="font-semibold">Exercise Time:</span> {franklinSite.generatorExerciseTime}</p>}
                  </div>
                )}
                {(franklinSite.airFilter || franklinSite.fuelFilter1 || franklinSite.oilFilter1) && (
                  <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md p-2 space-y-1">
                    <p className="font-semibold text-emerald-900 text-xs uppercase tracking-wide">Filters</p>
                    {franklinSite.airFilter && <p><span className="font-semibold">Air:</span> {franklinSite.airFilter}</p>}
                    {franklinSite.fuelFilter1 && <p><span className="font-semibold">Fuel 1:</span> {franklinSite.fuelFilter1}</p>}
                    {franklinSite.fuelFilter2 && <p><span className="font-semibold">Fuel 2:</span> {franklinSite.fuelFilter2}</p>}
                    {franklinSite.oilFilter1 && <p><span className="font-semibold">Oil 1:</span> {franklinSite.oilFilter1}</p>}
                    {franklinSite.oilFilter2 && <p><span className="font-semibold">Oil 2:</span> {franklinSite.oilFilter2}</p>}
                  </div>
                )}
                {(franklinSite.oilType || franklinSite.fuelAdditive) && (
                  <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md p-2 space-y-1">
                    <p className="font-semibold text-emerald-900 text-xs uppercase tracking-wide">Fluids</p>
                    {franklinSite.fuelAdditive && <p><span className="font-semibold">Fuel Additive:</span> {franklinSite.fuelAdditive}</p>}
                    {franklinSite.oilType && <p><span className="font-semibold">Oil Type:</span> {franklinSite.oilType}</p>}
                    {franklinSite.oilAmount && <p><span className="font-semibold">Oil Amount:</span> {franklinSite.oilAmount}</p>}
                  </div>
                )}
              </div>
            ) : !serviceCall?.isCspire ? (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Address:</span> {client?.address}, {client?.city}, {client?.state} {client?.zipCode}
              </p>
            ) : (
              <div className="text-sm text-gray-600 mt-1 space-y-1">
                <p>
                  <span className="font-semibold">Site ID:</span> {cspireWorkOrder?.site?.siteId || 'N/A'}
                </p>
                <p>
                  <span className="font-semibold">Location:</span> {cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'N/A'}
                </p>
                {cspireWorkOrder?.site?.addressLine1 && (
                  <p>
                    <span className="font-semibold">Address:</span> {cspireWorkOrder.site.addressLine1}, {cspireWorkOrder.site.city}, {cspireWorkOrder.site.state} {cspireWorkOrder.site.zipCode}
                  </p>
                )}
                {cspireWorkOrder?.site && (cspireWorkOrder.site.generatorType || cspireWorkOrder.site.generatorModel) && (
                  <div className="mt-2 bg-indigo-50 border border-indigo-200 rounded-md p-2 space-y-1">
                    <p className="font-semibold text-indigo-900 text-xs uppercase tracking-wide">Equipment Details</p>
                    {cspireWorkOrder.site.generatorType && <p><span className="font-semibold">Type:</span> {cspireWorkOrder.site.generatorType}</p>}
                    {cspireWorkOrder.site.generatorModel && <p><span className="font-semibold">Model:</span> {cspireWorkOrder.site.generatorModel}</p>}
                    {cspireWorkOrder.site.generatorSerialNumber && <p><span className="font-semibold">Serial:</span> {cspireWorkOrder.site.generatorSerialNumber}</p>}
                    {cspireWorkOrder.site.generatorSize && <p><span className="font-semibold">Size:</span> {cspireWorkOrder.site.generatorSize}</p>}
                    {cspireWorkOrder.site.generatorCoolingType && <p><span className="font-semibold">Cooling:</span> {formatCoolingType(cspireWorkOrder.site.generatorCoolingType)}</p>}
                    {cspireWorkOrder.site.generatorExerciseTime && <p><span className="font-semibold">Exercise Time:</span> {cspireWorkOrder.site.generatorExerciseTime}</p>}
                    {(cspireWorkOrder.site.airFilter || cspireWorkOrder.site.fuelFilter1 || cspireWorkOrder.site.oilFilter1) && (
                      <>
                        <p className="font-semibold text-indigo-900 text-xs uppercase tracking-wide pt-1">Filters</p>
                        {cspireWorkOrder.site.airFilter && <p><span className="font-semibold">Air:</span> {cspireWorkOrder.site.airFilter}</p>}
                        {cspireWorkOrder.site.fuelFilter1 && <p><span className="font-semibold">Fuel 1:</span> {cspireWorkOrder.site.fuelFilter1}</p>}
                        {cspireWorkOrder.site.fuelFilter2 && <p><span className="font-semibold">Fuel 2:</span> {cspireWorkOrder.site.fuelFilter2}</p>}
                        {cspireWorkOrder.site.oilFilter1 && <p><span className="font-semibold">Oil 1:</span> {cspireWorkOrder.site.oilFilter1}</p>}
                        {cspireWorkOrder.site.oilFilter2 && <p><span className="font-semibold">Oil 2:</span> {cspireWorkOrder.site.oilFilter2}</p>}
                      </>
                    )}
                    {(cspireWorkOrder.site.oilType || cspireWorkOrder.site.fuelType) && (
                      <>
                        <p className="font-semibold text-indigo-900 text-xs uppercase tracking-wide pt-1">Fluids</p>
                        {cspireWorkOrder.site.fuelType && <p><span className="font-semibold">Fuel Type:</span> {cspireWorkOrder.site.fuelType}</p>}
                        {cspireWorkOrder.site.fuelAdditive && <p><span className="font-semibold">Fuel Additive:</span> {cspireWorkOrder.site.fuelAdditive}</p>}
                        {cspireWorkOrder.site.oilType && <p><span className="font-semibold">Oil Type:</span> {cspireWorkOrder.site.oilType}</p>}
                        {cspireWorkOrder.site.oilAmount && <p><span className="font-semibold">Oil Amount:</span> {cspireWorkOrder.site.oilAmount}</p>}
                      </>
                    )}
                    {cspireWorkOrder.site.batterySize && (
                      <>
                        <p className="font-semibold text-indigo-900 text-xs uppercase tracking-wide pt-1">Battery</p>
                        <p><span className="font-semibold">Size:</span> {cspireWorkOrder.site.batterySize}</p>
                        {cspireWorkOrder.site.batteryDateCode && <p><span className="font-semibold">Date Code:</span> {cspireWorkOrder.site.batteryDateCode}</p>}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {client?.generatorModel && client?.generatorSerialNumber && (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Current Generator:</span> {client.generatorModel} ({client.generatorSerialNumber})
              </p>
            )}
          </div>

          {/* Previous Visits */}
          {serviceCall?.visits && serviceCall.visits.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-yellow-700" />
                  <h4 className="text-sm font-semibold text-yellow-900">
                    Previous Visits ({serviceCall.visits.length})
                  </h4>
                </div>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {serviceCall.visits.map((visit, idx) => (
                  <div key={visit.id} className="bg-white rounded p-2 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                      <span className="text-gray-500">{formatDate(visit.visitDate)}</span>
                    </div>
                    {visit.technician && (
                      <p className="text-gray-600 mb-1">
                        Tech: {visit.technician.firstName} {visit.technician.lastName}
                      </p>
                    )}
                    {visit.workPerformed && (
                      <p className="text-gray-700">{visit.workPerformed}</p>
                    )}
                    {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                      <p className="text-yellow-700 mt-1 italic">
                        Return Reason: {visit.reasonForReturn}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Type - Hidden for visits */}
          {actionType !== 'visit' && (
            <div>
              <label className="label">
                Service Type
              </label>
              <select
                value={formData.typeId}
                onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
                className="select"
              >
                <option value="">Select service type...</option>
                {serviceCallTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Change if needed. Default is based on whether it's an oil change month.
              </p>
            </div>
          )}

          {serviceCall?.isCspire && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="label mb-0">
                  CSpire Billing
                </label>
                <label className="flex items-center gap-2 cursor-pointer py-1 px-3 rounded-xl active:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={cspireCustomBilling}
                    onChange={(e) => {
                      setCspireCustomBilling(e.target.checked);
                      if (e.target.checked) {
                        setFormData({ ...formData, cspireScheduleId: '' });
                      }
                      if (errors.cspireScheduleId) setErrors({ ...errors, cspireScheduleId: null });
                    }}
                    className="checkbox"
                  />
                  <span className="text-sm text-gray-700">Custom billing (not on schedule)</span>
                </label>
              </div>

              {!cspireCustomBilling ? (
                <>
                  <select
                    value={formData.cspireScheduleId}
                    onChange={(e) => {
                      setFormData({ ...formData, cspireScheduleId: e.target.value });
                      if (errors.cspireScheduleId) setErrors({ ...errors, cspireScheduleId: null });
                    }}
                    className={`select ${errors.cspireScheduleId ? 'input-error' : ''}`}
                  >
                    <option value="">Select schedule...</option>
                    {cspireSchedules.map((schedule) => (
                      <option key={schedule.id} value={schedule.id}>
                        {schedule.name}
                      </option>
                    ))}
                  </select>
                  {errors.cspireScheduleId && (
                    <p className="mt-1 text-sm text-red-600">{errors.cspireScheduleId}</p>
                  )}
                </>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-amber-800">
                    Enter line items for this custom repair/service. Admin will add pricing.
                  </p>
                  {customLineItems.length > 0 && (
                    <div className="space-y-2">
                      {customLineItems.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded-lg border border-amber-100">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">{item.description}</div>
                            <div className="text-xs text-gray-500">Qty: {item.quantity}{item.itemNumber ? ` | Item #: ${item.itemNumber}` : ''}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setCustomLineItems(prev => prev.filter((_, i) => i !== idx))}
                            className="icon-btn-danger flex-shrink-0 ml-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                    <div className="sm:col-span-7">
                      <input
                        type="text"
                        placeholder="Description of work/item..."
                        value={newLineItem.description}
                        onChange={(e) => setNewLineItem(prev => ({ ...prev, description: e.target.value }))}
                        className="input text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="Qty"
                        value={newLineItem.quantity}
                        onChange={(e) => setNewLineItem(prev => ({ ...prev, quantity: e.target.value }))}
                        className="input text-sm"
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <button
                        type="button"
                        onClick={() => {
                          if (!newLineItem.description.trim()) return;
                          setCustomLineItems(prev => [...prev, {
                            description: newLineItem.description.trim(),
                            quantity: newLineItem.quantity || '1',
                            itemNumber: ''
                          }]);
                          setNewLineItem({ description: '', quantity: '1' });
                        }}
                        disabled={!newLineItem.description.trim()}
                        className="btn btn-secondary w-full text-sm disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Work Performed */}
          <div>
            <label className="label">
              Work Performed <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.workPerformed}
              onChange={(e) => {
                setFormData({ ...formData, workPerformed: e.target.value });
                if (errors.workPerformed) setErrors({ ...errors, workPerformed: null });
              }}
              rows={4}
              className={`input text-lg ${
                errors.workPerformed ? 'input-error' : ''
              }`}
              placeholder="Describe the work performed..."
              required
            />
            {errors.workPerformed && (
              <p className="mt-1 text-sm text-red-600">{errors.workPerformed}</p>
            )}
          </div>

          {/* Service Metrics - Hidden for visits */}
          {actionType !== 'visit' && (
            <div className={`grid grid-cols-1 ${serviceCall?.isCspire ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'} gap-4`}>
              <div>
                <label className="label">
                  Generator Hours
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.generatorHours}
                  onChange={(e) => setFormData({ ...formData, generatorHours: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">
                  Mileage
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">
                  Labor Hours
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.laborHours}
                  onChange={(e) => setFormData({ ...formData, laborHours: e.target.value })}
                  className="input"
                  placeholder="0.00"
                />
              </div>
              {serviceCall?.isCspire && (
                <div>
                  <label className="label">
                    Fuel Level
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={formData.fuelLevel}
                    onChange={(e) => setFormData({ ...formData, fuelLevel: e.target.value })}
                    className="input"
                    placeholder='e.g. "3/4 tank" or "500 gal"'
                  />
                </div>
              )}
            </div>
          )}

          {/* Parts Used - Hidden for visits */}
          {actionType !== 'visit' && (
            <div>
              <label className="label">
                Parts Used
              </label>
            {formData.partsUsed.length > 0 && (
              <div className="mb-3 space-y-3">
                {formData.partsUsed.map((part, index) => {
                  const itemNumber = part.itemNumber || part.name || 'N/A';
                  const description = part.description || part.name || '';
                  const quantity = parseFloat(part.quantity) || 0;
                  const cost = parseFloat(part.cost) || 0;
                  return (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-gray-900">{itemNumber}</div>
                        {description && <div className="text-sm text-gray-600">{description}</div>}
                        <div className="text-sm text-gray-500 mt-1">
                          Qty: {quantity.toFixed(2)} @ {formatCurrency(cost)} = {formatCurrency(quantity * cost)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemovePart(index)}
                        className="icon-btn-danger flex-shrink-0"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="space-y-4">
              {/* Toggle between inventory search and custom part */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  {useCustomPart ? 'Custom Part Mode' : 'Inventory Search Mode'}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMode}
                  className="link-btn"
                >
                  {useCustomPart ? 'Search Inventory Instead' : 'Add Custom Part Instead'}
                </button>
              </div>

              {useCustomPart ? (
                // Custom part entry mode
                <div className="space-y-4">
                  <div>
                    <label className="label">Part Name/Number *</label>
                    <input
                      type="text"
                      placeholder="Enter part name or number"
                      value={newPart.name}
                      onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Description (optional)</label>
                    <input
                      type="text"
                      placeholder="Enter part description"
                      value={newPart.description}
                      onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Cost *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!newPart.name || !newPart.quantity || !newPart.cost}
                        className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-5 w-5 mr-1" />
                        Add Part
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Inventory search mode
                <>
                  <InventorySearch 
                    onSelect={handleInventorySelect}
                    placeholder="Search for parts by item number or description..."
                    resetKey={searchResetKey}
                  />
                  {selectedInventoryItem && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-base">
                      <div className="font-medium text-blue-900">{selectedInventoryItem.itemNumber}</div>
                      <div className="text-blue-700">{selectedInventoryItem.description}</div>
                      <div className="text-blue-600 mt-1">Price: ${parseFloat(selectedInventoryItem.price || selectedInventoryItem.cost).toFixed(2)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Quantity</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder="1.00"
                        value={newPart.quantity}
                        onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Price (override)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={selectedInventoryItem ? `$${parseFloat(selectedInventoryItem.price || selectedInventoryItem.cost).toFixed(2)}` : "Price"}
                        value={newPart.cost}
                        onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleAddPart}
                        disabled={!selectedInventoryItem || !newPart.quantity}
                        className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-5 w-5 mr-1" />
                        Add Part
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          )}

          {actionType !== 'visit' && serviceCall?.isCspire && cspireWorkOrder?.site?.id && (
            <div className="border-t pt-5 space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-indigo-900">C Spire — generator &amp; filters</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Saved to this site for billing. These values appear on the printed invoice.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">Generator model</label>
                  <input
                    type="text"
                    value={formData.generatorModel}
                    onChange={(e) => setFormData({ ...formData, generatorModel: e.target.value })}
                    className="input"
                    placeholder="e.g. Generac 22kW"
                  />
                </div>
                <div>
                  <label className="label">Serial number</label>
                  <input
                    type="text"
                    value={formData.generatorSerialNumber}
                    onChange={(e) => setFormData({ ...formData, generatorSerialNumber: e.target.value })}
                    className="input"
                    placeholder="Generator S/N"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="label">Air filter</label>
                  <input
                    type="text"
                    value={formData.airFilter}
                    onChange={(e) => setFormData({ ...formData, airFilter: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Fuel filter 1</label>
                  <input
                    type="text"
                    value={formData.fuelFilter1}
                    onChange={(e) => setFormData({ ...formData, fuelFilter1: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Fuel filter 2</label>
                  <input
                    type="text"
                    value={formData.fuelFilter2}
                    onChange={(e) => setFormData({ ...formData, fuelFilter2: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Oil filter 1</label>
                  <input
                    type="text"
                    value={formData.oilFilter1}
                    onChange={(e) => setFormData({ ...formData, oilFilter1: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Oil filter 2</label>
                  <input
                    type="text"
                    value={formData.oilFilter2}
                    onChange={(e) => setFormData({ ...formData, oilFilter2: e.target.value })}
                    className="input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Generator Information - Hidden for visits and CSpire */}
          {actionType !== 'visit' && !serviceCall?.isCspire && (
          <div className="border-t pt-5">
            <div className="flex items-center justify-between mb-3">
              <label className="label mb-0">
                Generator Information {!hasExistingGeneratorInfo && <span className="text-red-500">*</span>}
              </label>
              {!hasExistingGeneratorInfo && (
                <label htmlFor="generatorInfoUnreadable" className="flex items-center gap-3 cursor-pointer py-2 px-3 rounded-xl active:bg-gray-100">
                  <input
                    type="checkbox"
                    id="generatorInfoUnreadable"
                    checked={formData.generatorInfoUnreadable}
                    onChange={(e) => {
                      setFormData({ ...formData, generatorInfoUnreadable: e.target.checked });
                      setErrors({ ...errors, generatorModel: null, generatorSerialNumber: null });
                    }}
                    className="checkbox"
                  />
                  <span className="text-base text-gray-600">Unreadable/Missing</span>
                </label>
              )}
            </div>
            {!formData.generatorInfoUnreadable && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    Model {!hasExistingGeneratorInfo && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.generatorModel}
                    onChange={(e) => {
                      setFormData({ ...formData, generatorModel: e.target.value });
                      if (errors.generatorModel) setErrors({ ...errors, generatorModel: null });
                    }}
                    className={`input ${
                      errors.generatorModel ? 'input-error' : ''
                    }`}
                    placeholder="e.g., Generac 22kW"
                    disabled={formData.generatorInfoUnreadable}
                  />
                  {errors.generatorModel && (
                    <p className="mt-1 text-sm text-red-600">{errors.generatorModel}</p>
                  )}
                </div>
                <div>
                  <label className="label">
                    Serial Number {!hasExistingGeneratorInfo && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={formData.generatorSerialNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, generatorSerialNumber: e.target.value });
                      if (errors.generatorSerialNumber) setErrors({ ...errors, generatorSerialNumber: null });
                    }}
                    className={`input ${
                      errors.generatorSerialNumber ? 'input-error' : ''
                    }`}
                    placeholder="e.g., 12345678"
                    disabled={formData.generatorInfoUnreadable}
                  />
                  {errors.generatorSerialNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.generatorSerialNumber}</p>
                  )}
                </div>
              </div>
            )}
            {formData.generatorInfoUnreadable && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5 mr-2" />
                  <p className="text-sm text-yellow-800">
                    Generator information marked as unreadable. Please ensure you've checked thoroughly before proceeding.
                  </p>
                </div>
              </div>
            )}
            {!hasExistingGeneratorInfo && !formData.generatorInfoUnreadable && (
              <p className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Important:</span> Please add generator information if available. This helps with future service calls.
              </p>
            )}
          </div>
          )}

          {/* Return Reason (only shown when marking as return) */}
          {actionType === 'return' && (
            <div>
              <label className="label">
                Return Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.returnReason}
                onChange={(e) => {
                  setFormData({ ...formData, returnReason: e.target.value });
                  if (errors.returnReason) setErrors({ ...errors, returnReason: null });
                }}
                rows={3}
                className={`input ${
                  errors.returnReason ? 'input-error' : ''
                }`}
                placeholder="Explain why this service call needs to be returned (e.g., need to order part, generator inaccessible, etc.)"
                required
              />
              {errors.returnReason && (
                <p className="mt-1 text-sm text-red-600">{errors.returnReason}</p>
              )}
            </div>
          )}

            </div>
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary w-full sm:w-auto"
              >
                Cancel
              </button>
              {localStorage.getItem(`serviceCallDraft_${serviceCall?.id}`) && (
                <button
                  type="button"
                  onClick={handleClearDraft}
                  className="btn w-full sm:w-auto border border-red-300 text-red-700 bg-white hover:bg-red-50"
                  title="Clear saved draft and start fresh"
                >
                  <X className="h-4 w-4 inline mr-1.5" />
                  Clear Draft
                </button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSaveProgress();
                }}
                disabled={isSavingProgress || updateServiceCallMutation.isLoading}
                className="btn w-full sm:w-auto bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
                title="Save parts and notes without completing (handoff to admin happens when you complete)"
              >
                <Save className="h-4 w-4 inline mr-1.5" />
                {isSavingProgress ? 'Saving…' : 'Save progress'}
              </button>
              {!serviceCall?.needsTechInfo && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setActionType('visit');
                      handleSubmit(e, 'visit');
                    }}
                    disabled={isSavingProgress || updateServiceCallMutation.isLoading}
                    className={`btn w-full sm:w-auto border ${
                      actionType === 'visit'
                        ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700'
                        : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                    } disabled:opacity-50`}
                  >
                    <FileText className="h-4 w-4 inline mr-1.5" />
                    Mark as Visit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setActionType('return');
                      handleSubmit(e, 'return');
                    }}
                    disabled={isSavingProgress || updateServiceCallMutation.isLoading}
                    className={`btn w-full sm:w-auto border ${
                      actionType === 'return'
                        ? 'bg-yellow-600 text-white border-yellow-600 hover:bg-yellow-700'
                        : 'bg-white text-yellow-700 border-yellow-300 hover:bg-yellow-50'
                    } disabled:opacity-50`}
                  >
                    <RotateCcw className="h-4 w-4 inline mr-1.5" />
                    Mark as Return
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setActionType('complete');
                  handleSubmit(e, 'complete');
                }}
                disabled={isSavingProgress || updateServiceCallMutation.isLoading}
                className="btn w-full sm:w-auto bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4 inline mr-1.5" />
                {updateServiceCallMutation.isLoading
                  ? 'Saving...'
                  : serviceCall?.needsTechInfo
                    ? 'Record Service Call Again'
                    : 'Complete Service'}
              </button>
            </div>
          </div>
            </form>
        ) : activeTab === 'client' ? (
          <form onSubmit={handleClientSubmit} className="flex h-full min-h-0 flex-col">
            <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
            {/* Client Editing Form */}
            <div className="space-y-5">
              {/* Generator Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Wrench className="h-5 w-5 mr-2 text-blue-600" />
                  Generator Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Type</label>
                    <input
                      type="text"
                      name="generatorType"
                      value={clientFormData.generatorType}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., Standby"
                    />
                  </div>
                  <div>
                    <label className="label">Model</label>
                    <input
                      type="text"
                      name="generatorModel"
                      value={clientFormData.generatorModel}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Model number"
                    />
                  </div>
                  <div>
                    <label className="label">Serial Number</label>
                    <input
                      type="text"
                      name="generatorSerialNumber"
                      value={clientFormData.generatorSerialNumber}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Serial number"
                    />
                  </div>
                  <div>
                    <label className="label">Size</label>
                    <input
                      type="text"
                      name="generatorSize"
                      value={clientFormData.generatorSize}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 50kW"
                    />
                  </div>
                  <div>
                    <label className="label">Cooling Type</label>
                    <select
                      name="generatorCoolingType"
                      value={clientFormData.generatorCoolingType}
                      onChange={handleClientChange}
                      className="input"
                    >
                      <option value="">Unknown</option>
                      <option value="air">Air-cooled</option>
                      <option value="water">Water-cooled</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="label">Exercise Time</label>
                    <input
                      type="text"
                      name="generatorExerciseTime"
                      value={clientFormData.generatorExerciseTime}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 2:00 PM"
                    />
                  </div>
                </div>
              </div>

              {!serviceCall?.isCspire && (
              <>
              {/* Gate / Access Code */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <KeyRound className="h-5 w-5 mr-2 text-amber-600" />
                  Gate / Access Code
                </h4>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer py-2">
                    <div className="relative inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={clientFormData.gateCode}
                        onChange={(e) => setClientFormData(prev => ({
                          ...prev,
                          gateCode: e.target.checked,
                          gateCodeValue: e.target.checked ? prev.gateCodeValue : ''
                        }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </div>
                    <span className="text-base font-medium text-gray-700">Gate code required</span>
                  </label>
                  {clientFormData.gateCode && (
                    <div>
                      <label className="label">Gate Code</label>
                      <input
                        type="text"
                        name="gateCodeValue"
                        value={clientFormData.gateCodeValue}
                        onChange={handleClientChange}
                        className="input"
                        placeholder="Enter gate code or access instructions"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Flags */}
              {flagsData?.data?.flags?.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <h4 className="text-base font-semibold text-gray-900 mb-4">Customer Flags</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {flagsData.data.flags.filter(f => f.isActive).map((flag) => {
                      const isSelected = flagIds.includes(flag.id);
                      return (
                        <label
                          key={flag.id}
                          className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-base cursor-pointer transition-colors active:scale-[0.98] ${
                            isSelected
                              ? 'border-blue-400 bg-blue-50 text-blue-700'
                              : 'border-gray-200 text-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setFlagIds(prev =>
                                isSelected ? prev.filter(id => id !== flag.id) : [...prev, flag.id]
                              );
                            }}
                            className="checkbox"
                          />
                          <FlagIcon iconName={flag.icon} className="h-5 w-5" title={flag.name} />
                          <span>{flag.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              </>
              )}

              {/* Filters & Fluids */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Wrench className="h-5 w-5 mr-2 text-amber-600" />
                  Filters & Fluids
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Air Filter</label>
                    <input
                      type="text"
                      name="airFilter"
                      value={clientFormData.airFilter}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Filter 1</label>
                    <input
                      type="text"
                      name="fuelFilter1"
                      value={clientFormData.fuelFilter1}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Filter 2</label>
                    <input
                      type="text"
                      name="fuelFilter2"
                      value={clientFormData.fuelFilter2}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Oil Filter 1</label>
                    <input
                      type="text"
                      name="oilFilter1"
                      value={clientFormData.oilFilter1}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Oil Filter 2</label>
                    <input
                      type="text"
                      name="oilFilter2"
                      value={clientFormData.oilFilter2}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Part number"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Additive</label>
                    <input
                      type="text"
                      name="fuelAdditive"
                      value={clientFormData.fuelAdditive}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="Type or brand"
                    />
                  </div>
                  <div>
                    <label className="label">Fuel Type</label>
                    <input
                      type="text"
                      name="fuelType"
                      value={clientFormData.fuelType}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., Diesel"
                    />
                  </div>
                  <div>
                    <label className="label">Oil Type</label>
                    <input
                      type="text"
                      name="oilType"
                      value={clientFormData.oilType}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 15W-40"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Oil Amount</label>
                    <input
                      type="text"
                      name="oilAmount"
                      value={clientFormData.oilAmount}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., 5 quarts"
                    />
                  </div>
                </div>
              </div>

              {/* Battery Information */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Battery className="h-5 w-5 mr-2 text-green-600" />
                  Battery Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="label">Battery Size</label>
                    <input
                      type="text"
                      name="batterySize"
                      value={clientFormData.batterySize}
                      onChange={handleClientChange}
                      className="input"
                      placeholder="e.g., Group 24"
                    />
                  </div>
                  <div>
                    <label className="label">Date Code</label>
                    <input
                      type="text"
                      name="batteryDateCode"
                      value={clientFormData.batteryDateCode}
                      onChange={handleClientChange}
                      maxLength={5}
                      className="input"
                      placeholder="MM/YY"
                    />
                  </div>
                  <div>
                    <label className="label">Date Changed</label>
                    <input
                      type="date"
                      name="batteryDateChanged"
                      value={clientFormData.batteryDateChanged}
                      onChange={handleClientChange}
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>

            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateClientMutation.isLoading || isSavingSiteInfo}
                className="btn btn-primary disabled:opacity-50"
              >
                {(updateClientMutation.isLoading || isSavingSiteInfo) ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSaveTechNotes} className="flex h-full min-h-0 flex-col">
            <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-6 py-6 space-y-5">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <StickyNote className="h-5 w-5 mt-0.5 text-yellow-700 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-yellow-900">Tech Notes</h4>
                    <p className="text-sm text-yellow-800 mt-1">
                      Short note that every tech sees on the assignment card every time
                      they service this {serviceCall?.isFranklin ? 'Franklin site' : serviceCall?.isCspire ? 'CSpire site' : 'client'}.
                      Use this for things like gate quirks, dog warnings, parking notes, equipment access tips, etc.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="techNotesField" className="label">Note (visible to all techs)</label>
                <textarea
                  id="techNotesField"
                  value={techNotes}
                  onChange={(e) => setTechNotes(e.target.value)}
                  maxLength={1000}
                  rows={6}
                  className="input"
                  placeholder="e.g., Gate is on the left side of the driveway, code is 1234. Watch out for the dog around the back of the building."
                />
                <p className="mt-1 text-xs text-gray-500 text-right">
                  {techNotes.length}/1000
                </p>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
              <button
                type="button"
                onClick={() => setTechNotes(initialTechNotes)}
                disabled={isSavingTechNotes || techNotes === initialTechNotes}
                className="btn btn-secondary disabled:opacity-50"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={isSavingTechNotes || (techNotes || '').trim() === (initialTechNotes || '').trim()}
                className="btn btn-primary disabled:opacity-50"
              >
                {isSavingTechNotes ? 'Saving...' : 'Save Tech Notes'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

// Service History Modal Component
const ServiceHistoryModal = ({ client, isOpen, onClose }) => {
  const { data: serviceHistoryData, isLoading } = useQuery(
    ['serviceHistory', client?.id],
    () => serviceCallsAPI.getServiceCalls({ clientId: client.id, limit: 1000 }),
    { enabled: isOpen && !!client }
  );

  const serviceCalls = serviceHistoryData?.data?.serviceCalls || [];
  const serviceCallIds = serviceCalls.map((serviceCall) => serviceCall.id);
  const { data: serviceHistoryDetailsData, isLoading: isLoadingHistoryDetails } = useQuery(
    ['serviceHistoryDetails', client?.id, serviceCallIds],
    async () => {
      const responses = await Promise.all(
        serviceCallIds.map((id) => serviceCallsAPI.getServiceCall(id))
      );
      return responses
        .map((response) => response.data?.serviceCall)
        .filter(Boolean);
    },
    { enabled: isOpen && !!client && serviceCallIds.length > 0 }
  );
  const fullServiceCalls = serviceHistoryDetailsData?.length ? serviceHistoryDetailsData : serviceCalls;

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-6 mx-auto w-full max-w-4xl rounded-2xl border border-slate-200 bg-white p-6 shadow-xl mb-10">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Service History</h3>
            <p className="text-sm text-gray-600 mt-1">{getClientDisplayName(client)}</p>
          </div>
          <button
            onClick={onClose}
            className="icon-btn"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="max-h-[600px] overflow-y-auto">
          {isLoading || isLoadingHistoryDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : fullServiceCalls.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No Service History</h3>
              <p className="mt-2 text-sm text-gray-500">
                This client doesn't have any service calls yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {fullServiceCalls.map((serviceCall) => {
                const displayDate = serviceCall.completedDate || serviceCall.scheduledDate || serviceCall.createdAt;
                const visits = Array.isArray(serviceCall.visits) ? serviceCall.visits : [];

                return (
                  <div key={serviceCall.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const rd = getResolutionDisplay(serviceCall);
                          if (rd) {
                            return (
                              <span className={`status-badge ${rd.color}`}>
                                {rd.label}
                              </span>
                            );
                          }
                          return (
                            <span className={`status-badge ${getStatusColor(serviceCall.status)}`}>
                              {getStatusText(serviceCall.status)}
                            </span>
                          );
                        })()}
                        {serviceCall.type && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {serviceCall.type.name}
                          </span>
                        )}
                        {serviceCall.isWarranty && (
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                            Warranty
                          </span>
                        )}
                      </div>
                      {displayDate && (
                        <span className="text-xs text-gray-500">
                          {formatDate(displayDate)}
                        </span>
                      )}
                    </div>

                    {visits.length > 0 ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <History className="h-5 w-5 text-yellow-700" />
                          <h4 className="font-semibold text-gray-900">Visit History ({visits.length} visit{visits.length > 1 ? 's' : ''})</h4>
                        </div>
                        <div className="space-y-3">
                          {visits.map((visit) => (
                            <div key={visit.id} className="bg-white rounded-md p-3 border border-yellow-100">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                                <span className="text-sm text-gray-600">{formatDate(visit.visitDate)}</span>
                              </div>
                              {visit.technician && (
                                <p className="text-sm text-gray-600 mb-2">
                                  <span className="font-medium">Technician:</span> {visit.technician.firstName} {visit.technician.lastName}
                                </p>
                              )}
                              {visit.type && (
                                <p className="text-sm text-gray-600 mb-2">
                                  <span className="font-medium">Type:</span> {visit.type.name}
                                </p>
                              )}
                              {visit.workPerformed && (
                                <div className="mb-2">
                                  <p className="text-sm font-medium text-gray-700 mb-1">Work Performed:</p>
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{visit.workPerformed}</p>
                                </div>
                              )}
                              {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                                <div className="bg-yellow-50 rounded p-2 mt-2">
                                  <p className="text-sm font-medium text-yellow-900 mb-1">Reason for Return:</p>
                                  <p className="text-sm text-yellow-800">{visit.reasonForReturn}</p>
                                </div>
                              )}
                              {visit.partsUsed && visit.partsUsed.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-sm font-medium text-gray-700 mb-1">Parts Used:</p>
                                  <ul className="text-sm text-gray-600 list-disc list-inside">
                                    {visit.partsUsed.map((part, partIdx) => (
                                      <li key={partIdx}>
                                        {part.itemNumber || part.name} - Qty: {part.quantity} {part.cost && `($${parseFloat(part.cost).toFixed(2)})`}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No visit history recorded.</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end border-t pt-4">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedClientForHistory, setSelectedClientForHistory] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedClientForDetails, setSelectedClientForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedSiteForDetails, setSelectedSiteForDetails] = useState(null);
  const [selectedSiteType, setSelectedSiteType] = useState(null);
  const [selectedWorkOrderForDetails, setSelectedWorkOrderForDetails] = useState(null);
  const [isSiteDetailsModalOpen, setIsSiteDetailsModalOpen] = useState(false);
  const [expandedPriorityId, setExpandedPriorityId] = useState(null);
  const [expandedAssignedId, setExpandedAssignedId] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const queryClient = useQueryClient();

  // Fetch priority assignments
  const { data: priorityAssignmentsData, isLoading: isLoadingPriority } = useQuery(
    'priorityAssignments',
    () => serviceCallsAPI.getPriorityAssignments(),
    { 
      enabled: !!user,
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Fetch weekly assignments (assigned clients) - only show 'assigned' status
  const { data: assignmentsData, isLoading } = useQuery(
    'weeklyAssignments',
    () => serviceCallsAPI.getWeeklyAssignments(),
    { 
      enabled: !!user,
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnWindowFocus: true
    }
  );

  const { data: attentionNeededData, isLoading: isLoadingAttentionNeeded } = useQuery(
    ['attentionNeededServiceCalls', user?.id],
    () => serviceCallsAPI.getServiceCalls({
      technicianId: user?.id,
      needsTechInfo: true,
      status: 'completed',
      limit: 1000
    }),
    {
      enabled: !!user,
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Filter priority assignments to only show assigned service calls
  const priorityAssignments = (priorityAssignmentsData?.data?.assignments || []).filter(
    sc => sc.status === 'assigned' || sc.status === 'in_progress'
  );

  // Filter to only show assigned service calls (not completed or returned)
  const assignments = (assignmentsData?.data?.assignments || []).filter(
    sc => sc.status === 'assigned' || sc.status === 'in_progress'
  );

  const attentionNeededCalls = useMemo(() => {
    const calls = attentionNeededData?.data?.serviceCalls || [];
    return [...calls].sort((a, b) => {
      const aTime = a.techInfoRequestedAt ? new Date(a.techInfoRequestedAt).getTime() : 0;
      const bTime = b.techInfoRequestedAt ? new Date(b.techInfoRequestedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [attentionNeededData]);

  const handleRecordService = async (serviceCall) => {
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      setSelectedServiceCall(response.data.serviceCall);
      setIsServiceModalOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load service call');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'return':
        return <RotateCcw className="h-5 w-5 text-yellow-500" />;
      case 'assigned':
        return <AlertTriangle className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="mt-1 text-base text-slate-500">
            Your assigned clients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <List className="h-4 w-4" />
              List
            </button>
            <button
              type="button"
              onClick={() => setViewMode('map')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <MapPin className="h-4 w-4" />
              Map
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'map' ? (
        <AssignmentsMap
          priorityAssignments={priorityAssignments}
          assignments={assignments}
        />
      ) : (
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="card overflow-hidden border-2 border-yellow-200">
            <div className="card-header bg-yellow-50 border-yellow-200">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <h3 className="text-lg font-semibold text-yellow-900">
                  Attention Needed ({attentionNeededCalls.length})
                </h3>
              </div>
            </div>
            <div className="card-body">
          {isLoadingAttentionNeeded ? (
            <div className="text-center py-8 text-gray-500">
              Loading attention needed...
            </div>
          ) : attentionNeededCalls.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No service calls need attention
            </div>
          ) : (
            <div className="flow-root">
              <ul className="-my-4 divide-y divide-slate-200">
                {attentionNeededCalls.map((serviceCall) => {
                  const client = serviceCall.client;
                  const cspireWorkOrder = serviceCall.cspireWorkOrder;
                  const displayName = serviceCall.isCspire
                    ? `CSpire - ${cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'Site'}`
                    : getClientDisplayName(client);
                  return (
                    <li key={serviceCall.id} className="py-5">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0 mt-1">
                              <AlertCircle className="h-5 w-5 text-yellow-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-base font-medium text-gray-900 break-words">
                                  {displayName}
                                </p>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                  Awaiting update
                                </span>
                                {serviceCall.isCspire && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-300">
                                    CSpire
                                  </span>
                                )}
                                {serviceCall.isFranklin && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
                                    Franklin
                                  </span>
                                )}
                                {serviceCall.isWarranty && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                                    <ShieldCheck className="h-3 w-3" />
                                    Warranty
                                  </span>
                                )}
                              </div>
                              <div className="mt-2">
                                {!serviceCall.isCspire && <ClientFlagIcons flags={client?.flags} />}
                              </div>
                              {serviceCall.techInfoRequestReason && (
                                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                  <p className="text-sm text-yellow-900 font-medium">Request from admin</p>
                                  <p className="text-sm text-yellow-800 whitespace-pre-wrap mt-1">
                                    {serviceCall.techInfoRequestReason}
                                  </p>
                                  {serviceCall.techInfoRequestedAt && (
                                    <p className="text-xs text-yellow-700 mt-2">
                                      Requested: {formatDateTime(serviceCall.techInfoRequestedAt)}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleRecordService(serviceCall)}
                              className="btn bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500 whitespace-nowrap"
                            >
                              <FileText className="h-4 w-4 mr-1.5" />
                              Record Service Call Again
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Priority Clients List */}
      {priorityAssignments.length > 0 && (
        <div className="card overflow-hidden border-2 border-red-200">
          <div className="card-header bg-red-50 border-red-200">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h3 className="text-lg font-semibold text-red-900">
                Repair Service Calls ({priorityAssignments.length})
              </h3>
            </div>
          </div>
          <div className="card-body">
            {isLoadingPriority ? (
              <div className="text-center py-8 text-gray-500">
                Loading priority assignments...
              </div>
            ) : priorityAssignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No repair customers assigned to you
              </div>
            ) : (
              <div className="flow-root">
                <ul className="-my-4 divide-y divide-slate-200">
                  {priorityAssignments.map((serviceCall) => {
                    const client = serviceCall.client;
                    const cspireWorkOrder = serviceCall.cspireWorkOrder;
                    const franklinSite = serviceCall.franklinSite;
                    const cspireSite = cspireWorkOrder?.site;
                    const cspireCoords = formatCspireCoords(cspireSite);
                    const cspireMapUrl = getCspireMapUrl(cspireSite);
                    const displayName = serviceCall.isFranklin
                      ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
                      : serviceCall.isCspire
                      ? `CSpire - ${cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'Site'}`
                      : getClientDisplayName(client);
                    const isExpanded = expandedPriorityId === serviceCall.id;
                    const techNote = serviceCall.isFranklin
                      ? franklinSite?.notes
                      : serviceCall.isCspire
                        ? cspireSite?.notes
                        : client?.notes;

                    return (
                      <li key={serviceCall.id} className="py-4">
                        <div className="rounded-2xl border border-slate-200 bg-white">
                          <button
                            type="button"
                            onClick={() => setExpandedPriorityId(isExpanded ? null : serviceCall.id)}
                            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 mt-1">
                                <AlertTriangle className="h-5 w-5 text-red-500" />
                              </div>
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-base font-semibold text-gray-900">{displayName}</p>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                                    Repair
                                  </span>
                                  {serviceCall.isCspire && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-300">
                                      CSpire
                                    </span>
                                  )}
                                  {serviceCall.isFranklin && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
                                      Franklin
                                    </span>
                                  )}
                                  {serviceCall.isWarranty && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                                      <ShieldCheck className="h-3 w-3" />
                                      Warranty
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                  {serviceCall.isFranklin
                                    ? [franklinSite?.city, franklinSite?.state].filter(Boolean).join(', ')
                                    : serviceCall.isCspire
                                    ? (cspireWorkOrder?.site?.city && cspireWorkOrder?.site?.state
                                      ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
                                      : (cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || ''))
                                    : [client?.city, client?.state].filter(Boolean).join(', ')}
                                </div>
                                {client?.gateCode && client?.gateCodeValue && (
                                  <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                                    <KeyRound className="h-3.5 w-3.5 text-amber-600" />
                                    Gate: {client.gateCodeValue}
                                  </div>
                                )}
                                {techNote && (
                                  <div className="mt-2 flex items-start gap-1.5 rounded-md border border-yellow-300 bg-yellow-50 px-2.5 py-1.5 text-sm text-yellow-900">
                                    <StickyNote className="h-4 w-4 mt-0.5 text-yellow-700 flex-shrink-0" />
                                    <span className="whitespace-pre-wrap break-words">
                                      <span className="font-semibold">Tech Note:</span> {techNote}
                                    </span>
                                  </div>
                                )}
                                <div className="mt-2">
                                  {!serviceCall.isCspire && !serviceCall.isFranklin && <ClientFlagIcons flags={client?.flags} />}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-slate-400">
                              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </div>
                          </button>
                          {isExpanded && (
                            <div className="border-t border-slate-200 px-5 py-4">
                              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div className="flex-1 space-y-2 text-sm text-gray-600">
                                  {client?.gateCode && client?.gateCodeValue && (
                                    <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mb-2">
                                      <KeyRound className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                      <span className="text-sm font-semibold text-amber-900">Gate Code: {client.gateCodeValue}</span>
                                    </div>
                                  )}
                                  {serviceCall.isFranklin && franklinSite ? (
                                    <>
                                      <p><span className="font-medium">Site:</span> {franklinSite.siteId}</p>
                                      <p><span className="font-medium">Company:</span> {franklinSite.company}</p>
                                      <p><span className="font-medium">Location:</span> {franklinSite.locationName || 'N/A'}</p>
                                      {franklinSite.address && (
                                        <p><span className="font-medium">Address:</span> {franklinSite.address}, {franklinSite.city}, {franklinSite.state} {franklinSite.zipCode}</p>
                                      )}
                                      {franklinSite.backupPower && (
                                        <p><span className="font-medium">Backup Power:</span> {franklinSite.backupPower}</p>
                                      )}
                                      {(franklinSite.latitude && franklinSite.longitude) && (
                                        <p>
                                          <span className="font-medium">Coordinates:</span>{' '}
                                          <a href={`https://maps.google.com/?q=${franklinSite.latitude},${franklinSite.longitude}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                            {franklinSite.latitude}, {franklinSite.longitude}
                                          </a>
                                        </p>
                                      )}
                                    </>
                                  ) : serviceCall.isCspire ? (
                                    <>
                                      <p><span className="font-medium">Site:</span> {cspireWorkOrder?.site?.siteId || 'N/A'}</p>
                                      <p><span className="font-medium">Location:</span> {cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'N/A'}</p>
                                      {cspireWorkOrder?.site?.addressLine1 && (
                                        <p><span className="font-medium">Address:</span> {cspireWorkOrder.site.addressLine1}, {cspireWorkOrder.site.city}, {cspireWorkOrder.site.state} {cspireWorkOrder.site.zipCode}</p>
                                      )}
                                      <p>
                                        <span className="font-medium">Coordinates:</span>{' '}
                                        {cspireCoords ? (
                                          <a href={cspireMapUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                            {cspireCoords}
                                          </a>
                                        ) : (
                                          'N/A'
                                        )}
                                      </p>
                                      {cspireWorkOrder?.site?.generatorType && <p><span className="font-medium">Generator:</span> {cspireWorkOrder.site.generatorType} {cspireWorkOrder.site.generatorModel && `- ${cspireWorkOrder.site.generatorModel}`}</p>}
                                      {cspireWorkOrder?.site?.generatorSerialNumber && <p><span className="font-medium">Serial:</span> {cspireWorkOrder.site.generatorSerialNumber}</p>}
                                    </>
                                  ) : (
                                    <>
                                      <p><span className="font-medium">Contact:</span> {client.contactFirstName} {client.contactLastName}</p>
                                      <p><span className="font-medium">Phone:</span> <a href={`tel:${client.phone}`} className="text-blue-600 hover:underline">{client.phone}</a></p>
                                      <p><span className="font-medium">Address:</span> {client.address}, {client.city}, {client.state} {client.zipCode}</p>
                                    </>
                                  )}
                                  {client?.generatorModel && client?.generatorSerialNumber && (
                                    <p><span className="font-medium">Generator:</span> {client.generatorModel} ({client.generatorSerialNumber})</p>
                                  )}
                                  {client && !client.generatorModel && !client.generatorSerialNumber && !serviceCall.isFranklin && (
                                    <p className="text-amber-600 font-medium">⚠️ Generator info missing - please add when recording service</p>
                                  )}
                                  {serviceCall.customerNotes && !serviceCall.isCspire && !serviceCall.isFranklin && (
                                    <p><span className="font-medium">Notes:</span> {serviceCall.customerNotes}</p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-3">
                                  {(client?.oilType || client?.oilAmount || client?.oilFilter1 || client?.oilFilter2) && (
                                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-1">
                                      {client?.oilType && <p><span className="font-medium">Oil Type:</span> {client.oilType}</p>}
                                      {client?.oilAmount && <p><span className="font-medium">Oil Amount:</span> {client.oilAmount}</p>}
                                      {(client?.oilFilter1 || client?.oilFilter2) && (
                                        <p><span className="font-medium">Oil Filter:</span> {[client?.oilFilter1, client?.oilFilter2].filter(Boolean).join(', ')}</p>
                                      )}
                                    </div>
                                  )}
                                  {(() => {
                                    const dirUrl = serviceCall.isFranklin && franklinSite
                                      ? getDirectionsUrl({ latitude: franklinSite.latitude, longitude: franklinSite.longitude, address: franklinSite.address, city: franklinSite.city, state: franklinSite.state, zipCode: franklinSite.zipCode })
                                      : serviceCall.isCspire && cspireSite
                                        ? getDirectionsUrl({ latitude: cspireSite.latitude, longitude: cspireSite.longitude, address: cspireSite.addressLine1, city: cspireSite.city, state: cspireSite.state, zipCode: cspireSite.zipCode })
                                        : client
                                          ? getDirectionsUrl({ latitude: client.latitude, longitude: client.longitude, address: client.address, city: client.city, state: client.state, zipCode: client.zipCode })
                                          : null;
                                    return dirUrl ? (
                                      <a
                                        href={dirUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400 btn-sm whitespace-nowrap"
                                      >
                                        <Navigation className="h-4 w-4 mr-1.5" />
                                        Directions
                                      </a>
                                    ) : null;
                                  })()}
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        if (serviceCall.isFranklin && franklinSite) {
                                          setSelectedSiteForDetails(franklinSite);
                                          setSelectedSiteType('franklin');
                                          setSelectedWorkOrderForDetails(null);
                                          setIsSiteDetailsModalOpen(true);
                                        } else if (serviceCall.isCspire && cspireWorkOrder?.site) {
                                          setSelectedSiteForDetails(cspireWorkOrder.site);
                                          setSelectedSiteType('cspire');
                                          setSelectedWorkOrderForDetails(cspireWorkOrder);
                                          setIsSiteDetailsModalOpen(true);
                                        } else if (client) {
                                          setSelectedClientForDetails(client);
                                          setIsDetailsModalOpen(true);
                                        }
                                      }}
                                      className="btn btn-secondary btn-sm disabled:opacity-50"
                                      disabled={!client && !franklinSite && !cspireWorkOrder?.site}
                                    >
                                      <Info className="h-4 w-4 mr-1.5" />
                                      Details
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedClientForHistory(client);
                                        setIsHistoryModalOpen(true);
                                      }}
                                      className="btn btn-secondary btn-sm"
                                      disabled={!client}
                                    >
                                      <History className="h-4 w-4 mr-1.5" />
                                      History
                                    </button>
                                  </div>
                                  <button
                                    onClick={() => handleRecordService(serviceCall)}
                                    className="btn bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 whitespace-nowrap"
                                  >
                                    <FileText className="h-4 w-4 mr-1.5" />
                                    Record Repair Service
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
        </div>

        <div className="space-y-6">
          {/* Assigned Clients List */}
          <div className="card overflow-hidden">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-slate-900">
                Assigned Clients ({assignments.length})
              </h3>
            </div>
            <div className="card-body">
          
          {isLoading ? (
            <div className="text-center py-8 text-slate-500">
              Loading...
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No clients assigned to you
            </div>
          ) : (
            <div className="flow-root">
              <ul className="-my-4 divide-y divide-slate-200">
                {assignments.map((serviceCall) => {
                  const client = serviceCall.client;
                  const cspireWorkOrder = serviceCall.cspireWorkOrder;
                  const franklinSite = serviceCall.franklinSite;
                  const cspireSite = cspireWorkOrder?.site;
                  const cspireCoords = formatCspireCoords(cspireSite);
                  const cspireMapUrl = getCspireMapUrl(cspireSite);
                  const displayName = serviceCall.isFranklin
                    ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
                    : serviceCall.isCspire
                    ? `CSpire - ${cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'Site'}`
                    : getClientDisplayName(client);
                  const isOilChangeMonth = client?.isOilChangeMonth || false;
                  const isExpanded = expandedAssignedId === serviceCall.id;
                  const techNote = serviceCall.isFranklin
                    ? franklinSite?.notes
                    : serviceCall.isCspire
                      ? cspireSite?.notes
                      : client?.notes;

                  return (
                    <li key={serviceCall.id} className="py-4">
                      <div className="rounded-2xl border border-slate-200 bg-white">
                        <button
                          type="button"
                          onClick={() => setExpandedAssignedId(isExpanded ? null : serviceCall.id)}
                          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              {getStatusIcon(serviceCall.status)}
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-base font-semibold text-gray-900">{displayName}</p>
                                {isOilChangeMonth ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                    Service
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                    Inspection
                                  </span>
                                )}
                                {serviceCall.isCspire && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-300">
                                    CSpire
                                  </span>
                                )}
                                {serviceCall.isFranklin && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
                                    Franklin
                                  </span>
                                )}
                                {serviceCall.isWarranty && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                                    <ShieldCheck className="h-3 w-3" />
                                    Warranty
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                {serviceCall.isFranklin
                                  ? [franklinSite?.city, franklinSite?.state].filter(Boolean).join(', ')
                                  : serviceCall.isCspire
                                  ? (cspireWorkOrder?.site?.city && cspireWorkOrder?.site?.state
                                    ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
                                    : (cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || ''))
                                  : [client?.city, client?.state].filter(Boolean).join(', ')}
                              </div>
                              {client?.gateCode && client?.gateCodeValue && (
                                <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                                  <KeyRound className="h-3.5 w-3.5 text-amber-600" />
                                  Gate: {client.gateCodeValue}
                                </div>
                              )}
                              {techNote && (
                                <div className="mt-2 flex items-start gap-1.5 rounded-md border border-yellow-300 bg-yellow-50 px-2.5 py-1.5 text-sm text-yellow-900">
                                  <StickyNote className="h-4 w-4 mt-0.5 text-yellow-700 flex-shrink-0" />
                                  <span className="whitespace-pre-wrap break-words">
                                    <span className="font-semibold">Tech Note:</span> {techNote}
                                  </span>
                                </div>
                              )}
                              <div className="mt-2">
                                {!serviceCall.isCspire && <ClientFlagIcons flags={client?.flags} />}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400">
                            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="border-t border-slate-200 px-5 py-4">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                              <div className="flex-1 space-y-2 text-sm text-gray-600">
                                {client?.gateCode && client?.gateCodeValue && (
                                  <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mb-2">
                                    <KeyRound className="h-4 w-4 text-amber-600 flex-shrink-0" />
                                    <span className="text-sm font-semibold text-amber-900">Gate Code: {client.gateCodeValue}</span>
                                  </div>
                                )}
                                {serviceCall.isFranklin && franklinSite ? (
                                  <>
                                    <p><span className="font-medium">Site:</span> {franklinSite.siteId}</p>
                                    <p><span className="font-medium">Company:</span> {franklinSite.company}</p>
                                    <p><span className="font-medium">Location:</span> {franklinSite.locationName || 'N/A'}</p>
                                    {franklinSite.address && (
                                      <p><span className="font-medium">Address:</span> {franklinSite.address}, {franklinSite.city}, {franklinSite.state} {franklinSite.zipCode}</p>
                                    )}
                                    {franklinSite.backupPower && (
                                      <p><span className="font-medium">Backup Power:</span> {franklinSite.backupPower}</p>
                                    )}
                                    {(franklinSite.latitude && franklinSite.longitude) && (
                                      <p>
                                        <span className="font-medium">Coordinates:</span>{' '}
                                        <a href={`https://maps.google.com/?q=${franklinSite.latitude},${franklinSite.longitude}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                          {franklinSite.latitude}, {franklinSite.longitude}
                                        </a>
                                      </p>
                                    )}
                                  </>
                                ) : !serviceCall.isCspire ? (
                                  <>
                                    <p><span className="font-medium">Contact:</span> {client.contactFirstName} {client.contactLastName}</p>
                                    <p><span className="font-medium">Phone:</span> <a href={`tel:${client.phone}`} className="text-blue-600 hover:underline">{client.phone}</a></p>
                                    <p><span className="font-medium">Address:</span> {client.address}, {client.city}, {client.state} {client.zipCode}</p>
                                  </>
                                ) : (
                                  <>
                                    <p><span className="font-medium">Site:</span> {cspireWorkOrder?.site?.siteId || 'N/A'}</p>
                                    <p><span className="font-medium">Location:</span> {cspireWorkOrder?.locationName || cspireWorkOrder?.site?.locationName || 'N/A'}</p>
                                    {cspireWorkOrder?.site?.addressLine1 && (
                                      <p><span className="font-medium">Address:</span> {cspireWorkOrder.site.addressLine1}, {cspireWorkOrder.site.city}, {cspireWorkOrder.site.state} {cspireWorkOrder.site.zipCode}</p>
                                    )}
                                    <p>
                                      <span className="font-medium">Coordinates:</span>{' '}
                                      {cspireCoords ? (
                                        <a href={cspireMapUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                          {cspireCoords}
                                        </a>
                                      ) : (
                                        'N/A'
                                      )}
                                    </p>
                                  </>
                                )}
                                {client?.generatorModel && client?.generatorSerialNumber && (
                                  <p><span className="font-medium">Generator:</span> {client.generatorModel} ({client.generatorSerialNumber})</p>
                                )}
                                {client && !client.generatorModel && !client.generatorSerialNumber && !serviceCall.isFranklin && (
                                  <p className="text-amber-600 font-medium">⚠️ Generator info missing - please add when recording service</p>
                                )}
                                {serviceCall.customerNotes && !serviceCall.isCspire && !serviceCall.isFranklin && (
                                  <p><span className="font-medium">Notes:</span> {serviceCall.customerNotes}</p>
                                )}
                              </div>
                              <div className="flex flex-col gap-3">
                                {(client?.oilType || client?.oilAmount || client?.oilFilter1 || client?.oilFilter2) && (
                                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-1">
                                    {client?.oilType && <p><span className="font-medium">Oil Type:</span> {client.oilType}</p>}
                                    {client?.oilAmount && <p><span className="font-medium">Oil Amount:</span> {client.oilAmount}</p>}
                                    {(client?.oilFilter1 || client?.oilFilter2) && (
                                      <p><span className="font-medium">Oil Filter:</span> {[client?.oilFilter1, client?.oilFilter2].filter(Boolean).join(', ')}</p>
                                    )}
                                  </div>
                                )}
                                {(() => {
                                  const dirUrl = serviceCall.isFranklin && franklinSite
                                    ? getDirectionsUrl({ latitude: franklinSite.latitude, longitude: franklinSite.longitude, address: franklinSite.address, city: franklinSite.city, state: franklinSite.state, zipCode: franklinSite.zipCode })
                                    : serviceCall.isCspire && cspireSite
                                      ? getDirectionsUrl({ latitude: cspireSite.latitude, longitude: cspireSite.longitude, address: cspireSite.addressLine1, city: cspireSite.city, state: cspireSite.state, zipCode: cspireSite.zipCode })
                                      : client
                                        ? getDirectionsUrl({ latitude: client.latitude, longitude: client.longitude, address: client.address, city: client.city, state: client.state, zipCode: client.zipCode })
                                        : null;
                                  return dirUrl ? (
                                    <a
                                      href={dirUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400 btn-sm whitespace-nowrap"
                                    >
                                      <Navigation className="h-4 w-4 mr-1.5" />
                                      Directions
                                    </a>
                                  ) : null;
                                })()}
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      if (serviceCall.isFranklin && franklinSite) {
                                        setSelectedSiteForDetails(franklinSite);
                                        setSelectedSiteType('franklin');
                                        setSelectedWorkOrderForDetails(null);
                                        setIsSiteDetailsModalOpen(true);
                                      } else if (serviceCall.isCspire && cspireWorkOrder?.site) {
                                        setSelectedSiteForDetails(cspireWorkOrder.site);
                                        setSelectedSiteType('cspire');
                                        setSelectedWorkOrderForDetails(cspireWorkOrder);
                                        setIsSiteDetailsModalOpen(true);
                                      } else if (client) {
                                        setSelectedClientForDetails(client);
                                        setIsDetailsModalOpen(true);
                                      }
                                    }}
                                    className="btn btn-secondary btn-sm disabled:opacity-50"
                                    disabled={!client && !franklinSite && !cspireWorkOrder?.site}
                                  >
                                    <Info className="h-4 w-4 mr-1.5" />
                                    Details
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedClientForHistory(client);
                                      setIsHistoryModalOpen(true);
                                    }}
                                    className="btn btn-secondary btn-sm"
                                    disabled={!client}
                                  >
                                    <History className="h-4 w-4 mr-1.5" />
                                    History
                                  </button>
                                </div>
                                <button
                                  onClick={() => handleRecordService(serviceCall)}
                                  className="btn bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 whitespace-nowrap"
                                >
                                  <FileText className="h-4 w-4 mr-1.5" />
                                  Record Service
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
        </div>
      </div>
      )}

      {/* Service Call Modal */}
      {selectedServiceCall && (
        <ServiceCallModal
          serviceCall={selectedServiceCall}
          isOpen={isServiceModalOpen}
          isRepairCall={priorityAssignments.some(sc => sc.id === selectedServiceCall.id)}
          onServiceCallRefresh={(sc) => setSelectedServiceCall(sc)}
          onClose={() => {
            setIsServiceModalOpen(false);
            setSelectedServiceCall(null);
          }}
        />
      )}

      {/* Service History Modal */}
      <ServiceHistoryModal
        client={selectedClientForHistory}
        isOpen={isHistoryModalOpen}
        onClose={() => {
          setIsHistoryModalOpen(false);
          setSelectedClientForHistory(null);
        }}
      />

      {/* Client Details Modal */}
      <ClientDetailsModal
        client={selectedClientForDetails}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedClientForDetails(null);
        }}
      />

      {/* Site Details Modal (Franklin / CSpire) */}
      <SiteDetailsModal
        site={selectedSiteForDetails}
        siteType={selectedSiteType}
        workOrder={selectedWorkOrderForDetails}
        isOpen={isSiteDetailsModalOpen}
        onClose={() => {
          setIsSiteDetailsModalOpen(false);
          setSelectedSiteForDetails(null);
          setSelectedSiteType(null);
          setSelectedWorkOrderForDetails(null);
        }}
      />
    </div>
  );
}
